import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { parseCSV, downloadCsv as downloadSample } from '../utils/csv';
import { useAuth } from '../context/AuthContext';
import { StaffProfile } from './Onboarding';

// parseCSV and the sample-file download now live in utils/csv.js, so this
// screen and the material-acknowledgement bulk upload share ONE parser. Two
// copies would drift, and the quoted-newline handling in there was a bug fix
// that should not have to be made twice.

// ─── CSV Upload Modal ─────────────────────────────────────────────────────────
//
// Props:
//   title        — modal heading
//   sampleFile   — { name, content } for sample download
//   columns      — [{ key, label }] shown in preview
//   mapRow(raw)  — fn: raw CSV row → mapped object (return null to skip)
//   onUpload(rows) — async fn: sends mapped rows to API, returns { inserted, skipped, errors[] }
//   onDone       — called after successful import to refresh parent
//   onClose
//   formatHint   — JSX shown under the file picker describing expected columns
//
//   optionLabel  — optional tick-box shown on the preview step; its value is
//                  passed to onUpload(rows, optionChecked)
export function CSVUploadModal({ title, sampleFile, columns, mapRow, onUpload, onDone, onClose, formatHint, optionLabel }) {
  const [option,  setOption]  = useState(false);
  const [step,    setStep]    = useState('pick');    // pick → preview → result
  const [rows,    setRows]    = useState([]);
  const [warnings,setWarnings]= useState([]);
  const [result,  setResult]  = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      const { rows: raw } = parseCSV(evt.target.result);
      const mapped   = [];
      const warns    = [];
      raw.forEach((r, i) => {
        const m = mapRow(r);
        if (m === null) warns.push(`Row ${i + 2}: skipped (missing required field)`);
        else mapped.push(m);
      });
      setRows(mapped); setWarnings(warns); setErr('');
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const doUpload = async () => {
    setBusy(true); setErr('');
    try {
      const res = await onUpload(rows, option);
      setResult(res); setStep('result');
    } catch(e) { setErr(e.response?.data?.error || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep('pick'); setRows([]); setWarnings([]); setResult(null); setErr(''); };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* ── Step 1: Pick file ── */}
        {step === 'pick' && (
          <>
            {/* Format hint */}
            <div style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:16, fontSize:13 }}>
              <div style={{ fontWeight:600, marginBottom:8, color:'var(--text2)', fontSize:12, textTransform:'uppercase', letterSpacing:'0.05em' }}>Expected CSV format</div>
              {formatHint}
              {/* Only offer the download when there is something to download.
                  Without this guard a caller that forgets sampleFile still gets
                  the button, and clicking it throws instead of doing nothing
                  visible — which is how the training topics upload appeared
                  broken. */}
              {sampleFile && sampleFile.content && (
                <button className="btn btn-secondary btn-sm" style={{ marginTop:10 }}
                  onClick={() => downloadSample(sampleFile.name || 'sample.csv', sampleFile.content)}>
                  ⬇ Download Sample CSV
                </button>
              )}
            </div>
            <div className="form-group">
              <label>Select CSV File (.csv)</label>
              <input type="file" accept=".csv,text/csv" onChange={handleFile}
                style={{ padding:'8px 0', background:'transparent', border:'none', color:'var(--text)' }} />
            </div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {/* ── Step 2: Preview ── */}
        {step === 'preview' && (
          <>
            <div style={{ display:'flex', gap:16, marginBottom:14 }}>
              <div style={{ padding:'8px 16px', background:'rgba(79,142,247,0.1)', borderRadius:8, textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:700, color:'var(--accent)' }}>{rows.length}</div>
                <div style={{ fontSize:11, color:'var(--text2)' }}>Rows to import</div>
              </div>
              {warnings.length > 0 && (
                <div style={{ padding:'8px 16px', background:'rgba(245,191,36,0.1)', borderRadius:8, textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:700, color:'var(--amber)' }}>{warnings.length}</div>
                  <div style={{ fontSize:11, color:'var(--text2)' }}>Rows skipped</div>
                </div>
              )}
            </div>

            {warnings.length > 0 && (
              <div style={{ background:'rgba(245,191,36,0.08)', border:'1px solid rgba(245,191,36,0.2)', borderRadius:'var(--radius)', padding:'8px 12px', marginBottom:14, fontSize:12, color:'var(--amber)' }}>
                {warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}

            {optionLabel && (
              <label className="opt" style={{ marginBottom:14, fontSize:12.5 }}>
                <input type="checkbox" checked={option} onChange={e => setOption(e.target.checked)} />
                <span>{optionLabel}</span>
              </label>
            )}

            {rows.length === 0 ? (
              <div className="empty-state"><p>No valid rows found. Check the file format.</p></div>
            ) : (
              <div className="table-wrap" style={{ maxHeight:280, overflowY:'auto', marginBottom:14 }}>
                <table>
                  <thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
                  <tbody>
                    {rows.slice(0, 50).map((row, i) => (
                      <tr key={i}>{columns.map(c => <td key={c.key} style={{ fontSize:12 }}>{row[c.key] || <span style={{ color:'var(--text2)' }}>—</span>}</td>)}</tr>
                    ))}
                    {rows.length > 50 && (
                      <tr><td colSpan={columns.length} style={{ textAlign:'center', color:'var(--text2)', fontSize:12, padding:8 }}>…and {rows.length - 50} more rows</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={reset}>← Back</button>
              <button className="btn btn-primary" onClick={doUpload} disabled={busy || rows.length === 0}>
                {busy ? 'Importing…' : `Import ${rows.length} Records`}
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Result ── */}
        {step === 'result' && result && (
          <>
            <div style={{ display:'flex', gap:16, marginBottom:16 }}>
              <div style={{ flex:1, padding:'16px', background:'rgba(52,211,153,0.1)', borderRadius:10, textAlign:'center' }}>
                <div style={{ fontSize:28, fontWeight:700, color:'var(--green)' }}>{result.inserted}</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>Inserted</div>
              </div>
              <div style={{ flex:1, padding:'16px', background:'rgba(245,191,36,0.1)', borderRadius:10, textAlign:'center' }}>
                <div style={{ fontSize:28, fontWeight:700, color:'var(--amber)' }}>{result.skipped}</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>Skipped (duplicates / not found)</div>
              </div>
            </div>

            {/* Rows already in the portal — the re-uploaded-report case. Shown
                separately from errors: nothing went wrong, they were skipped. */}
            {result.duplicate_details?.length > 0 && (
              <div style={{ background:'rgba(245,191,36,0.10)', border:'1px solid rgba(245,191,36,0.30)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:14, fontSize:12, color:'#8a6100', maxHeight:180, overflowY:'auto' }}>
                <div style={{ fontWeight:600, marginBottom:6 }}>
                  Already in the portal — {result.duplicates} row(s) skipped:
                </div>
                {result.duplicate_details.map((d, i) => <div key={i} style={{ marginBottom:3 }}>• {d}</div>)}
                <div style={{ marginTop:6, opacity:0.85 }}>
                  Matched on campus + title + work date. If these really are separate jobs,
                  re-upload with “Import duplicates anyway” ticked.
                </div>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--red)', maxHeight:160, overflowY:'auto' }}>
                <div style={{ fontWeight:600, marginBottom:6 }}>Errors:</div>
                {result.errors.map((e, i) => <div key={i} style={{ marginBottom:3 }}>• {e}</div>)}
              </div>
            )}

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={reset}>Upload Another</button>
              <button className="btn btn-primary" onClick={() => { onDone(); onClose(); }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function StatusBadge({ active }) {
  return <span className={`badge ${active ? 'badge-completed' : 'badge-faulty'}`}>{active ? 'Active' : 'Inactive'}</span>;
}

function ActionButtons({ item, onEdit, onToggle }) {
  return (
    <div className="flex-gap" style={{ gap: 6 }}>
      <button className="btn btn-secondary btn-sm" onClick={() => onEdit(item)}>Edit</button>
      <button className={`btn btn-sm ${item.active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => onToggle(item)}>
        {item.active ? 'Deactivate' : 'Activate'}
      </button>
    </div>
  );
}

// Campus selector used in multiple tabs
function CampusSelector({ campuses, value, onChange, extraRight }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ minWidth: 220 }}>
        <option value="">— Select campus —</option>
        {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {extraRight}
    </div>
  );
}

function EmptyState({ message }) {
  return <div className="empty-state"><p>{message}</p></div>;
}

// ─── Campus Tab ───────────────────────────────────────────────────────────────

function CampusTab() {
  const [campuses, setCampuses] = useState([]);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ code: '', name: '', city: '' });
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState('');

  const load = async () => {
    try { const r = await api.get('/master/campuses'); setCampuses(r.data); } catch(e) {}
  };
  useEffect(() => { load(); }, []);

  const openNew  = () => { setForm({ code:'', name:'', city:'', health_check_required:true, staff_all_campuses:false }); setEditing(null); setErr(''); setModal(true); };
  const openEdit = c => { setForm({ code: c.code, name: c.name, city: c.city||'', active: c.active,
    health_check_required: c.health_check_required !== false, staff_all_campuses: !!c.staff_all_campuses }); setEditing(c); setErr(''); setModal(true); };

  const save = async () => {
    if (!form.name.trim()) { setErr('Campus name is required'); return; }
    if (!editing && !form.code.trim()) { setErr('Campus code is required'); return; }
    setBusy(true); setErr('');
    try {
      if (editing) {
        await api.put(`/master/campuses/${editing.id}`, { name: form.name.trim(), city: form.city.trim(), active: form.active,
          health_check_required: form.health_check_required, staff_all_campuses: form.staff_all_campuses });
      } else {
        await api.post('/master/campuses', { code: form.code.trim(), name: form.name.trim(), city: form.city.trim() });
      }
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async c => {
    try { await api.put(`/master/campuses/${c.id}`, { active: !c.active }); load(); } catch(e) {}
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button className="btn btn-primary" onClick={openNew}>+ Add Campus</button>
      </div>
      {!campuses.length ? <EmptyState message="No campuses found" /> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>City</th><th>Health Check</th><th>Staff Scope</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {campuses.map(c => (
                  <tr key={c.id} style={{ opacity: c.active ? 1 : 0.55 }}>
                    <td><span className="mono" style={{ fontSize:12 }}>{c.code}</span></td>
                    <td style={{ fontWeight:500 }}>{c.name}</td>
                    <td style={{ color:'var(--text2)', fontSize:13 }}>{c.city || '—'}</td>
                    <td style={{ fontSize:12 }}>{c.health_check_required === false ? <span style={{ color:'var(--text2)' }}>Not required</span> : 'Required'}</td>
                    <td style={{ fontSize:12 }}>{c.staff_all_campuses ? <span style={{ color:'var(--accent)' }}>All campuses</span> : 'This campus'}</td>
                    <td><StatusBadge active={c.active} /></td>
                    <td><ActionButtons item={c} onEdit={openEdit} onToggle={toggleActive} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:460 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Campus' : 'Add Campus'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Campus Code {editing ? <span style={{ fontSize:11, color:'var(--text2)' }}>(cannot change)</span> : '*'}</label>
                <input value={form.code} disabled={!!editing}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. CBE-MAIN" maxLength={20} />
              </div>
              <div className="form-group">
                <label>Campus Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Coimbatore Main" />
              </div>
            </div>
            <div className="form-group">
              <label>City</label>
              <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="e.g. Coimbatore" />
            </div>
            {editing && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            {editing && (<>
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0, marginBottom:8 }}>
                <input type="checkbox" checked={form.health_check_required} onChange={e => setForm(f => ({ ...f, health_check_required: e.target.checked }))} />
                Requires daily health check
                <span style={{ fontSize:11, color:'var(--text2)' }}>(off for non-operational campuses like Corporate)</span>
              </label>
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0, marginBottom:8 }}>
                <input type="checkbox" checked={form.staff_all_campuses} onChange={e => setForm(f => ({ ...f, staff_all_campuses: e.target.checked }))} />
                Staff here are visible to all campuses
                <span style={{ fontSize:11, color:'var(--text2)' }}>(e.g. Corporate staff who travel between campuses)</span>
              </label>
            </>)}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Campus'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Generic campus-scoped tab (Rooms / Locations) ───────────────────────────

function CampusScopedTab({ campuses, apiBase, columns, formFields, title, itemLabel }) {
  const [selectedCampus, setSelectedCampus] = useState('');
  const [items,   setItems]   = useState([]);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({});
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  const emptyForm = useCallback(() => {
    const f = { campus_id: selectedCampus };
    formFields.forEach(field => { f[field.key] = ''; });
    return f;
  }, [selectedCampus, formFields]);

  const load = useCallback(async id => {
    if (!id) { setItems([]); return; }
    try { const r = await api.get(`${apiBase}/${id}`); setItems(r.data); } catch(e) {}
  }, [apiBase]);

  useEffect(() => { load(selectedCampus); }, [selectedCampus, load]);

  const openNew  = () => { setForm(emptyForm()); setEditing(null); setErr(''); setModal(true); };
  const openEdit = item => {
    const f = { campus_id: item.campus_id, active: item.active };
    formFields.forEach(field => { f[field.key] = item[field.key] || ''; });
    setForm(f); setEditing(item); setErr(''); setModal(true);
  };

  const save = async () => {
    const requiredField = formFields.find(f => f.required && !form[f.key]?.toString().trim());
    if (requiredField) { setErr(`${requiredField.label} is required`); return; }
    if (!form.campus_id) { setErr('Campus is required'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { campus_id: form.campus_id };
      formFields.forEach(f => { payload[f.key] = form[f.key]?.trim() || null; });
      if (editing) {
        const editPayload = { active: form.active };
        formFields.forEach(f => { editPayload[f.key] = form[f.key]?.trim() || null; });
        await api.put(`${apiBase.replace('/'+apiBase.split('/').pop(), '')}/${apiBase.split('/').pop()}/${editing.id}`, editPayload);
      } else {
        await api.post(`${apiBase.replace('/'+apiBase.split('/').pop(), '')}/${apiBase.split('/').pop()}`, payload);
      }
      setModal(false); load(selectedCampus);
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async item => {
    try {
      const base = apiBase.replace('/'+apiBase.split('/').pop(), '');
      const endpoint = apiBase.split('/').pop();
      await api.put(`${base}/${endpoint}/${item.id}`, { active: !item.active });
      load(selectedCampus);
    } catch(e) {}
  };

  const active   = items.filter(i => i.active);
  const inactive = items.filter(i => !i.active);

  return (
    <div>
      <CampusSelector campuses={campuses} value={selectedCampus} onChange={setSelectedCampus}
        extraRight={
          <>
            {selectedCampus && <button className="btn btn-primary" onClick={openNew}>+ Add {itemLabel}</button>}
            {selectedCampus && <span style={{ fontSize:12, color:'var(--text2)', marginLeft:'auto' }}>{active.length} active · {inactive.length} inactive</span>}
          </>
        }
      />

      {!selectedCampus ? <EmptyState message={`Select a campus to manage ${title.toLowerCase()}`} />
        : !items.length ? <EmptyState message={`No ${title.toLowerCase()} found for this campus`} />
        : (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}<th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} style={{ opacity: item.active ? 1 : 0.55 }}>
                      {columns.map(col => (
                        <td key={col.key} style={col.style || {}}>
                          {col.mono
                            ? <span className="mono" style={{ fontSize:12 }}>{item[col.key] || '—'}</span>
                            : <span style={col.muted ? { color:'var(--text2)', fontSize:13 } : { fontWeight: item.active ? 500 : 400 }}>{item[col.key] || '—'}</span>
                          }
                        </td>
                      ))}
                      <td><StatusBadge active={item.active} /></td>
                      <td><ActionButtons item={item} onEdit={openEdit} onToggle={toggleActive} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:460 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? `Edit ${itemLabel}` : `Add ${itemLabel}`}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            {!editing && (
              <div className="form-group">
                <label>Campus *</label>
                <select value={form.campus_id} onChange={e => setForm(f => ({ ...f, campus_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {formFields.map(field => (
              <div className="form-group" key={field.key}>
                <label>{field.label}{field.required ? ' *' : ''} {!field.required && <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span>}</label>
                <input
                  type={field.type || 'text'}
                  value={form[field.key] || ''}
                  onChange={e => setForm(f => ({ ...f, [field.key]: field.upper ? e.target.value.toUpperCase() : e.target.value }))}
                  placeholder={field.placeholder || ''}
                  maxLength={field.maxLength}
                />
              </div>
            ))}
            {editing && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : `Add ${itemLabel}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Staff Details Tab ────────────────────────────────────────────────────────

function StaffDetailsTab({ campuses, roles, allCampuses, isAdmin = true, onOpenProfile }) {
  const [selectedCampus, setSelectedCampus] = useState('');
  // The campus mappings a person can be placed into. Loaded once; both lists
  // are filtered to the staff member's own campus in the form.
  const [gwOus, setGwOus] = useState([]);
  const [gwGroups, setGwGroups] = useState([]);
  useEffect(() => {
    api.get('/gw/ous').then(r => setGwOus(r.data.filter(o => o.active))).catch(() => {});
    api.get('/gw/group-ids').then(r => setGwGroups(r.data)).catch(() => {});
  }, []);
  const [people,    setPeople]    = useState([]);
  const [modal,     setModal]     = useState(false);
  const [csvModal,  setCsvModal]  = useState(false);
  const [loginModal,setLoginModal]= useState(false);
  const [editing,   setEditing]   = useState(null);
  const [staffForLogin, setStaffForLogin] = useState(null);
  const [form,      setForm]      = useState({ campus_id:'', name:'', email:'', designation:'' });
  const [loginForm, setLoginForm] = useState({ username:'', password:'', full_name:'', email:'', role:'it_staff', permission_role_id:'', campus_ids:[] });
  const [busy,      setBusy]      = useState(false);
  const [err,       setErr]       = useState('');
  const [loginErr,  setLoginErr]  = useState('');
  const [selected,  setSelected]  = useState(new Set());

  const load = useCallback(async campusId => {
    if (!campusId) { setPeople([]); return; }
    try { const r = await api.get(`/master/informedby/${campusId}`); setPeople(r.data); } catch(e) {}
    setSelected(new Set());
  }, []);
  useEffect(() => { load(selectedCampus); }, [selectedCampus, load]);

  const openNew  = () => { setForm({ campus_id: selectedCampus, name:'', email:'', designation:'', campus_ou_id:'', group_ids:[] }); setEditing(null); setErr(''); setModal(true); };
  const openEdit = p  => { setForm({ campus_id: p.campus_id, name: p.name, email: p.email||'',
                                     designation: p.designation||'', active: p.active,
                                     campus_ou_id: p.campus_ou_id || '',
                                     group_ids: (p.gw_groups || []).map(g => g.id) });
                           setEditing(p); setErr(''); setModal(true); };

  const openLoginModal = staff => {
    const username = staff.name.toLowerCase().replace(/\s+/g, '.').replace(/[^a-z0-9.]/g,'');
    setStaffForLogin(staff);
    setLoginForm({ username, password:'', full_name: staff.name, email: staff.email||'', permission_role_id:'', campus_ids: selectedCampus ? [parseInt(selectedCampus)] : [] });
    setLoginErr(''); setLoginModal(true);
  };

  const save = async () => {
    if (!form.name.trim())   { setErr('Name is required'); return; }
    if (!form.campus_id)     { setErr('Campus is required'); return; }
    setBusy(true); setErr('');
    try {
      // campus_id included so staff can be moved to another campus from here
      if (editing) await api.put(`/master/informedby/${editing.id}`, { campus_id: parseInt(form.campus_id), name:form.name.trim(), email:form.email||null, designation:form.designation||null, active:form.active,
                                   campus_ou_id: form.campus_ou_id || null, group_ids: form.group_ids || [] });
      else {
        // The create endpoint takes the basics; the Google placement is saved
        // straight after, on the row it just made.
        const made = await api.post('/master/informedby', { campus_id:form.campus_id, name:form.name.trim(), email:form.email||null, designation:form.designation||null });
        const newId = made.data?.id;
        if (newId && (form.campus_ou_id || (form.group_ids || []).length)) {
          await api.put(`/master/informedby/${newId}`, {
            campus_ou_id: form.campus_ou_id || null, group_ids: form.group_ids || [] }).catch(() => {});
        }
      }
      setModal(false); load(selectedCampus);
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const saveLogin = async () => {
    if (!loginForm.full_name)  { setLoginErr('Name required'); return; }
    if (!loginForm.username)   { setLoginErr('Username required'); return; }
    if (!loginForm.permission_role_id) { setLoginErr('Select a role'); return; }
    if (!loginForm.campus_ids.length) { setLoginErr('Select at least one campus'); return; }
    setBusy(true); setLoginErr('');
    try {
      await api.post('/master/users', {
        username: loginForm.username,
        password: loginForm.password,
        full_name: loginForm.full_name,
        email: loginForm.email || null,
        campus_ids: loginForm.campus_ids,
        permission_role_id: loginForm.permission_role_id
      });
      setLoginModal(false);
    } catch(e) { setLoginErr(e.response?.data?.error || 'Failed to create login'); }
    finally { setBusy(false); }
  };

  const toggleActive = async p => {
    try { await api.put(`/master/informedby/${p.id}`, { active: !p.active }); load(selectedCampus); } catch(e) {}
  };
  const delStaff = async p => {
    if (!window.confirm(`Permanently delete staff "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/master/informedby/${p.id}`); load(selectedCampus); }
    catch(e) { window.alert(e.response?.data?.error || 'Delete failed'); }
  };

  const toggleCampusLogin = id => setLoginForm(f => ({ ...f, campus_ids: f.campus_ids.includes(id) ? f.campus_ids.filter(c => c !== id) : [...f.campus_ids, id] }));

  // Bulk
  const allIds = people.map(p => p.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleOne = id => { const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); setSelected(next); };
  const bulkActivate   = async val => { try { await api.post('/master/staff/bulk-update', { ids:[...selected], updates:{ active:val } }); load(selectedCampus); } catch(e) {} };

  const active   = people.filter(p => p.active);
  const inactive = people.filter(p => !p.active);

  return (
    <div>
      <CampusSelector campuses={campuses} value={selectedCampus} onChange={setSelectedCampus}
        extraRight={
          <>
            {selectedCampus && <button className="btn btn-primary" onClick={openNew}>+ Add Staff</button>}
            <button className="btn btn-secondary" onClick={() => setCsvModal(true)}>⬆ Upload CSV</button>
            {selected.size > 0 && (
              <>
                <button className="btn btn-secondary btn-sm" onClick={() => bulkActivate(true)}>Activate {selected.size}</button>
                <button className="btn btn-danger btn-sm"    onClick={() => bulkActivate(false)}>Deactivate {selected.size}</button>
              </>
            )}
            {selectedCampus && <span style={{ fontSize:12, color:'var(--text2)', marginLeft:'auto' }}>{active.length} active · {inactive.length} inactive</span>}
          </>
        }
      />

      {!selectedCampus ? <EmptyState message="Select a campus to manage staff details" />
        : !people.length ? <EmptyState message="No staff added for this campus" />
        : (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width:36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width:14, height:14 }} /></th>
                    <th>Name</th><th>Designation</th><th>Email</th><th>Google OU</th><th>Groups</th><th>Google check</th>{isAdmin && <th>Portal Login</th>}<th>Status</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map(p => (
                    <tr key={p.id} style={{ opacity: p.active ? 1 : 0.55, background: selected.has(p.id) ? 'rgba(79,142,247,0.06)' : '' }}>
                      <td><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} style={{ width:14, height:14 }} /></td>
                      {/* The name opens the person's profile — groups, assets
                          issued and their acknowledgement state in one place. */}
                      <td style={{ fontWeight: p.active ? 500 : 400 }}>
                        {onOpenProfile
                          ? <button type="button" onClick={() => onOpenProfile(p.id)}
                              style={{ background:'none', border:'none', padding:0, font:'inherit',
                                       color:'var(--accent)', cursor:'pointer', textAlign:'left' }}>
                              {p.name}
                            </button>
                          : p.name}
                      </td>
                      <td style={{ color:'var(--text2)', fontSize:13 }}>{p.designation || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{p.email || '—'}</td>
                      {/* Google placement, next to the address it belongs to. */}
                      <td style={{ fontSize:12 }}>{p.gw_ou_name
                        || <span style={{ color:'var(--text3)' }}>—</span>}</td>
                      <td style={{ fontSize:12 }}>{(p.gw_groups || []).length
                        ? p.gw_groups.map(g => g.name).join(', ')
                        : <span style={{ color:'var(--text3)' }}>—</span>}</td>
                      {/* Verify checks Google and hides itself once the person
                          passes. A failure keeps the button and shows why —
                          hiding it then would lose the accounts being hunted. */}
                      <td style={{ fontSize:11.5, minWidth:150 }}>
                        <GwVerifyCell person={p} onChanged={() => load(selectedCampus)} />
                      </td>
                      {/* Login status inline — no need to cross-check the Users page.
                          Creating logins stays with admins. */}
                      {isAdmin && <td>
                        {p.has_login
                          ? <span style={{ fontSize:12, color:'var(--green)', fontWeight:600 }}>✓ Has login</span>
                          : !p.email
                            ? <span style={{ fontSize:11, color:'var(--text3)' }} title="Add an email to create a login">— no email —</span>
                            : <button className="btn btn-primary btn-sm" onClick={() => openLoginModal(p)}
                                title="Create a portal login for this staff">🔑 Create Login</button>}
                      </td>}
                      <td><StatusBadge active={p.active} /></td>
                      <td>
                        <div className="flex-gap" style={{ gap:6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>Edit</button>
                          <button className={`btn btn-sm ${p.active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(p)}>{p.active ? 'Deactivate' : 'Activate'}</button>
                          <button className="btn btn-danger btn-sm" onClick={() => delStaff(p)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Edit/Add modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:480 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Staff' : 'Add Staff Member'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            {/* Editable on edit too, so staff can be moved between campuses */}
            <div className="form-group">
              <label>Campus *</label>
              <select value={form.campus_id} onChange={e => setForm(f => ({ ...f, campus_id: e.target.value }))}>
                <option value="">— Select —</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {editing && <span style={{ fontSize:11, color:'var(--text2)' }}>Changing this moves the staff member to another campus.</span>}
            </div>
            <div className="form-row">
              <div className="form-group"><label>Full Name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rajesh Kumar" /></div>
              <div className="form-group"><label>Designation</label><input value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} placeholder="e.g. Principal" /></div>
            </div>
            <div className="form-group"><label>Email <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>

            {/* Google placement. Both lists are limited to the campus chosen
                above, so nobody can be filed under another campus's unit. */}
            <div className="form-row">
              <div className="form-group"><label>Google OU</label>
                <select value={form.campus_ou_id || ''} disabled={!form.campus_id}
                        onChange={e => setForm(f => ({ ...f, campus_ou_id: e.target.value }))}>
                  <option value="">— none —</option>
                  {gwOus.filter(o => String(o.campus_id) === String(form.campus_id))
                        .map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                {form.campus_id && !gwOus.some(o => String(o.campus_id) === String(form.campus_id)) && (
                  <span style={{ fontSize:11, color:'var(--text2)' }}>
                    No units mapped for this campus yet — set them in Google OU Mapping.
                  </span>
                )}
              </div>
              <div className="form-group"><label>Google groups</label>
                <select multiple size={4} value={(form.group_ids || []).map(String)}
                        disabled={!form.campus_id}
                        onChange={e => setForm(f => ({ ...f,
                          group_ids: Array.from(e.target.selectedOptions).map(o => Number(o.value)) }))}>
                  {gwGroups.filter(g => String(g.campus_id) === String(form.campus_id))
                           .map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                <span style={{ fontSize:11, color:'var(--text2)' }}>Ctrl-click for more than one.</span>
              </div>
            </div>

            {editing && (
              <div className="form-group"><label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Staff'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Login modal */}
      {loginModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setLoginModal(false)}>
          <div className="modal" style={{ maxWidth:520 }}>
            <div className="modal-header">
              <span className="modal-title">Create Login — {staffForLogin?.name}</span>
              <button className="modal-close" onClick={() => setLoginModal(false)}>×</button>
            </div>
            <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14, padding:'8px 10px', background:'var(--bg3)', borderRadius:'var(--radius)' }}>
              Creating a portal login for <strong style={{ color:'var(--text)' }}>{staffForLogin?.name}</strong>. If you set a password they’ll change it on first login; leave it blank for Google sign-in only.
            </div>
            <div className="form-row">
              <div className="form-group"><label>Full Name *</label><input value={loginForm.full_name} onChange={e => setLoginForm(f => ({ ...f, full_name: e.target.value }))} /></div>
              <div className="form-group"><label>Email</label><input type="email" value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Username *</label><input value={loginForm.username} onChange={e => setLoginForm(f => ({ ...f, username: e.target.value.toLowerCase() }))} /></div>
              <div className="form-group"><label>Password (optional)</label><input type="password" value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} placeholder="Blank = Google sign-in only" /></div>
            </div>
            <div className="form-group"><label>Role *</label>
              <select value={loginForm.permission_role_id} onChange={e => setLoginForm(f => ({ ...f, permission_role_id: e.target.value }))}>
                <option value="">— Select a role —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>e.g. Staff Requester, IT Admin, HOS. Manage roles under the Roles tab.</div>
            </div>
            <div className="form-group">
              <label>Assign Campuses *</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, maxHeight:180, overflowY:'auto', padding:10, background:'var(--bg3)', borderRadius:'var(--radius)', border:'1px solid var(--border)' }}>
                {allCampuses.map(c => (
                  <label className="opt" key={c.id} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:'var(--text)', padding:'4px 6px', borderRadius:4, background: loginForm.campus_ids.includes(c.id) ? 'rgba(79,142,247,0.1)' : 'transparent' }}>
                    <input type="checkbox" checked={loginForm.campus_ids.includes(c.id)} onChange={() => toggleCampusLogin(c.id)} style={{ width:14, height:14 }} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            {loginErr && <div className="err-msg">{loginErr}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLoginModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveLogin} disabled={busy}>{busy ? 'Creating…' : 'Create Login'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {csvModal && (
        <CSVUploadModal
          title="Upload Staff Details — CSV"
          sampleFile={{
            name: 'staff_sample.csv',
            content: 'Name,Designation,Email,Campus\nRajesh Kumar,Principal,rajesh@school.edu,CBE-MAIN\nPriya Venkat,HOD,priya@school.edu,CBE-MAIN\n'
          }}
          columns={[
            { key:'name',        label:'Name' },
            { key:'designation', label:'Designation' },
            { key:'email',       label:'Email' },
            { key:'campus',      label:'Campus Code' },
          ]}
          mapRow={raw => {
            // Flexible header matching
            const name  = raw.name || raw.full_name || raw.staff_name || raw['name'] || '';
            const email = raw.email || raw.mail_id || raw.email_id || raw.mail || '';
            const desig = raw.designation || raw.title || raw.role || '';
            const camp  = raw.campus || raw.campus_code || raw.campus_name || '';
            if (!name.trim()) return null;
            return { name: name.trim(), designation: desig.trim(), email: email.trim(), campus: camp.trim() };
          }}
          onUpload={rows => api.post('/master/staff/import', { records: rows }).then(r => r.data)}
          onDone={() => load(selectedCampus)}
          onClose={() => setCsvModal(false)}
          formatHint={
            <div style={{ fontSize:12, lineHeight:1.7 }}>
              <strong>Required columns:</strong> Name, Campus (code like <span className="mono">CBE-MAIN</span>)<br />
              <strong>Optional columns:</strong> Designation, Email<br />
              <strong>Flexible headers:</strong> "Mail ID", "Email ID", "Staff Name" all accepted<br />
              Duplicate entries (same name + campus) are automatically skipped.
            </div>
          }
        />
      )}
    </div>
  );
}

// ─── Students Tab ───────────────────────────────────────────────────────────

function StudentsTab({ campuses }) {
  const [selectedCampus, setSelectedCampus] = useState('');
  const [students, setStudents] = useState([]);
  const [modal,    setModal]    = useState(false);
  const [csvModal, setCsvModal] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ campus_id:'', name:'', id_number:'', grade:'', section:'' });
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState('');
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async campusId => {
    if (!campusId) { setStudents([]); return; }
    try { const r = await api.get(`/master/students/${campusId}`); setStudents(r.data); } catch(e) {}
    setSelected(new Set());
  }, []);
  useEffect(() => { load(selectedCampus); }, [selectedCampus, load]);

  const openNew  = () => { setForm({ campus_id: selectedCampus, name:'', id_number:'', grade:'', section:'' }); setEditing(null); setErr(''); setModal(true); };
  const openEdit = s => { setForm({ campus_id: s.campus_id, name: s.name, id_number: s.id_number||'', grade: s.grade||'', section: s.section||'', active: s.active }); setEditing(s); setErr(''); setModal(true); };

  const save = async () => {
    if (!form.name.trim()) { setErr('Student name is required'); return; }
    if (!form.campus_id)   { setErr('Campus is required'); return; }
    setBusy(true); setErr('');
    try {
      if (editing) await api.put(`/master/students/${editing.id}`, { name:form.name.trim(), id_number:form.id_number||null, grade:form.grade||null, section:form.section||null, active:form.active });
      else         await api.post('/master/students', { campus_id:form.campus_id, name:form.name.trim(), id_number:form.id_number||null, grade:form.grade||null, section:form.section||null });
      setModal(false); load(selectedCampus);
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async s => { try { await api.put(`/master/students/${s.id}`, { active: !s.active }); load(selectedCampus); } catch(e) {} };
  const delStudent = async s => {
    if (!window.confirm(`Permanently delete student "${s.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/master/students/${s.id}`); load(selectedCampus); }
    catch(e) { window.alert(e.response?.data?.error || 'Delete failed'); }
  };

  const allIds = students.map(s => s.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleOne = id => { const n = new Set(selected); n.has(id)?n.delete(id):n.add(id); setSelected(n); };
  const bulkActive = async val => { try { await api.post('/master/students/bulk-update', { ids:[...selected], updates:{ active:val } }); load(selectedCampus); } catch(e) {} };

  const active = students.filter(s => s.active), inactive = students.filter(s => !s.active);

  return (
    <div>
      <CampusSelector campuses={campuses} value={selectedCampus} onChange={setSelectedCampus}
        extraRight={
          <>
            {selectedCampus && <button className="btn btn-primary" onClick={openNew}>+ Add Student</button>}
            <button className="btn btn-secondary" onClick={() => setCsvModal(true)}>⬆ Upload CSV</button>
            {selected.size > 0 && (
              <>
                <button className="btn btn-secondary btn-sm" onClick={() => bulkActive(true)}>Activate {selected.size}</button>
                <button className="btn btn-danger btn-sm"    onClick={() => bulkActive(false)}>Deactivate {selected.size}</button>
              </>
            )}
            {selectedCampus && <span style={{ fontSize:12, color:'var(--text2)', marginLeft:'auto' }}>{active.length} active · {inactive.length} inactive</span>}
          </>
        }
      />

      {!selectedCampus ? <EmptyState message="Select a campus to manage students" />
        : !students.length ? <EmptyState message="No students found for this campus" />
        : (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width:36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width:14, height:14 }} /></th>
                    <th>Name</th><th>ID Number</th><th>Grade</th><th>Section</th><th>Status</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(s => (
                    <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55, background: selected.has(s.id) ? 'rgba(79,142,247,0.06)' : '' }}>
                      <td><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleOne(s.id)} style={{ width:14, height:14 }} /></td>
                      <td style={{ fontWeight: s.active ? 500 : 400 }}>{s.name}</td>
                      <td><span className="mono" style={{ fontSize:12 }}>{s.id_number || '—'}</span></td>
                      <td style={{ fontSize:13 }}>{s.grade || '—'}</td>
                      <td style={{ fontSize:13 }}>{s.section || '—'}</td>
                      <td><StatusBadge active={s.active} /></td>
                      <td>
                        <div className="flex-gap" style={{ gap:6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}>Edit</button>
                          <button className={`btn btn-sm ${s.active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(s)}>{s.active ? 'Deactivate' : 'Activate'}</button>
                          <button className="btn btn-danger btn-sm" onClick={() => delStudent(s)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:480 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Student' : 'Add Student'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            {!editing && (
              <div className="form-group">
                <label>Campus *</label>
                <select value={form.campus_id} onChange={e => setForm(f => ({ ...f, campus_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-row">
              <div className="form-group"><label>Student Name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Arjun S" /></div>
              <div className="form-group"><label>ID Number</label><input className="mono" value={form.id_number} onChange={e => setForm(f => ({ ...f, id_number: e.target.value }))} placeholder="e.g. STU2024001" /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Grade</label><input value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} placeholder="e.g. 6" /></div>
              <div className="form-group"><label>Section</label><input value={form.section} onChange={e => setForm(f => ({ ...f, section: e.target.value }))} placeholder="e.g. A" /></div>
            </div>
            {editing && (
              <div className="form-group"><label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Student'}</button>
            </div>
          </div>
        </div>
      )}

      {csvModal && (
        <CSVUploadModal
          title="Upload Students — CSV"
          sampleFile={{
            name: 'students_sample.csv',
            content: 'Student Name,ID Number,Grade,Section,Campus\nArjun S,STU2024001,6,A,CBE-MAIN\nMeera R,STU2024002,6,B,CBE-MAIN\n'
          }}
          columns={[
            { key:'name',      label:'Name' },
            { key:'id_number', label:'ID Number' },
            { key:'grade',     label:'Grade' },
            { key:'section',   label:'Section' },
            { key:'campus',    label:'Campus' },
          ]}
          mapRow={raw => {
            const name   = raw.student_name || raw.name || raw.studentname || '';
            const idnum  = raw.id_number || raw.idnumber || raw.id || raw.admission_no || raw.admissionnumber || '';
            const grade  = raw.grade || raw.class || raw.std || raw.standard || '';
            const section= raw.section || raw.sec || raw.division || '';
            const camp   = raw.campus || raw.campus_code || raw.campusname || '';
            if (!name.trim()) return null;
            return { name:name.trim(), id_number:idnum.trim(), grade:grade.trim(), section:section.trim(), campus:camp.trim() };
          }}
          onUpload={rows => api.post('/master/students/import', { records: rows }).then(r => r.data)}
          onDone={() => load(selectedCampus)}
          onClose={() => setCsvModal(false)}
          formatHint={
            <div style={{ fontSize:12, lineHeight:1.7 }}>
              <strong>Required columns:</strong> Student Name, Campus (code like <span className="mono">CBE-MAIN</span> or campus name)<br />
              <strong>Optional columns:</strong> ID Number, Grade, Section<br />
              <strong>Flexible headers:</strong> "Class"/"Standard"→Grade, "Division"→Section, "Admission No"→ID Number<br />
              Duplicates (same ID Number, or same Name+Grade+Section per campus) are skipped.
            </div>
          }
        />
      )}
    </div>
  );
}

// ─── Brands Tab ───────────────────────────────────────────────────────────────

function BrandsTab() {
  const [brands,   setBrands]  = useState([]);
  const [modal,    setModal]   = useState(false);
  const [csvModal, setCsvModal]= useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ name:'' });
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  const load = async () => { try { const r = await api.get('/master/brands'); setBrands(r.data); } catch(e) {} };
  useEffect(() => { load(); }, []);

  const openNew  = () => { setForm({ name:'' }); setEditing(null); setErr(''); setModal(true); };
  const openEdit = b => { setForm({ name: b.name, active: b.active }); setEditing(b); setErr(''); setModal(true); };

  const save = async () => {
    if (!form.name.trim()) { setErr('Brand name is required'); return; }
    setBusy(true); setErr('');
    try {
      if (editing) await api.put(`/master/brands/${editing.id}`, { name: form.name.trim(), active: form.active });
      else         await api.post('/master/brands', { name: form.name.trim() });
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async b => {
    try { await api.put(`/master/brands/${b.id}`, { active: !b.active }); load(); } catch(e) {}
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'flex-end', marginBottom:16 }}>
        <button className="btn btn-secondary" onClick={() => setCsvModal(true)}>⬆ Upload CSV</button>
        <button className="btn btn-primary" onClick={openNew}>+ Add Brand</button>
      </div>
      {!brands.length ? <EmptyState message="No brands added yet" /> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>#</th><th>Brand Name</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {brands.map((b, i) => (
                  <tr key={b.id} style={{ opacity: b.active ? 1 : 0.55 }}>
                    <td style={{ color:'var(--text2)', fontSize:12 }}>{i + 1}</td>
                    <td style={{ fontWeight: b.active ? 500 : 400 }}>{b.name}</td>
                    <td><StatusBadge active={b.active} /></td>
                    <td><ActionButtons item={b} onEdit={openEdit} onToggle={toggleActive} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:380 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Brand' : 'Add Brand'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label>Brand Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Dell, HP, Lenovo" />
            </div>
            {editing && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Brand'}</button>
            </div>
          </div>
        </div>
      )}

      {csvModal && (
        <CSVUploadModal
          title="Upload Asset Brands — CSV"
          sampleFile={{
            name: 'brands_sample.csv',
            content: 'Brand\nDell\nHP\nLenovo\nApple\nCanon\n'
          }}
          columns={[{ key:'name', label:'Brand Name' }]}
          mapRow={raw => {
            const name = raw.brand || raw.brand_name || raw.name || raw['brand name'] || '';
            if (!name.trim()) return null;
            return { name: name.trim() };
          }}
          onUpload={rows => api.post('/master/brands/import', { brands: rows }).then(r => r.data)}
          onDone={load}
          onClose={() => setCsvModal(false)}
          formatHint={
            <div style={{ fontSize:12, lineHeight:1.7 }}>
              <strong>Single column:</strong> Brand (or "Brand Name")<br />
              One brand per row. Duplicates are automatically skipped.
            </div>
          }
        />
      )}
    </div>
  );
}

// ─── Models Tab ───────────────────────────────────────────────────────────────

function ModelsTab() {
  const [brands,        setBrands]       = useState([]);
  const [selectedBrand, setSelectedBrand]= useState('');
  const [models,        setModels]       = useState([]);
  const [modal,         setModal]        = useState(false);
  const [csvModal,      setCsvModal]     = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState({ brand_id:'', name:'' });
  const [busy,         setBusy]         = useState(false);
  const [err,          setErr]          = useState('');

  const loadBrands = async () => { try { const r = await api.get('/master/brands'); setBrands(r.data.filter(b => b.active)); } catch(e) {} };
  const loadModels = useCallback(async brandId => {
    if (!brandId) { setModels([]); return; }
    try { const r = await api.get(`/master/models/${brandId}`); setModels(r.data); } catch(e) {}
  }, []);

  useEffect(() => { loadBrands(); }, []);
  useEffect(() => { loadModels(selectedBrand); }, [selectedBrand, loadModels]);

  const openNew  = () => { setForm({ brand_id: selectedBrand, name:'' }); setEditing(null); setErr(''); setModal(true); };
  const openEdit = m => { setForm({ brand_id: m.brand_id, name: m.name, active: m.active }); setEditing(m); setErr(''); setModal(true); };

  const save = async () => {
    if (!form.name.trim()) { setErr('Model name is required'); return; }
    if (!form.brand_id)    { setErr('Brand is required'); return; }
    setBusy(true); setErr('');
    try {
      if (editing) await api.put(`/master/models/${editing.id}`, { name: form.name.trim(), active: form.active });
      else         await api.post('/master/models', { brand_id: form.brand_id, name: form.name.trim() });
      setModal(false); loadModels(selectedBrand);
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async m => {
    try { await api.put(`/master/models/${m.id}`, { active: !m.active }); loadModels(selectedBrand); } catch(e) {}
  };

  const active   = models.filter(m => m.active);
  const inactive = models.filter(m => !m.active);

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <select value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)} style={{ minWidth:220 }}>
          <option value="">— Select brand —</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {selectedBrand && <button className="btn btn-primary" onClick={openNew}>+ Add Model</button>}
        <button className="btn btn-secondary" onClick={() => setCsvModal(true)}>⬆ Upload CSV</button>
        {selectedBrand && <span style={{ fontSize:12, color:'var(--text2)', marginLeft:'auto' }}>{active.length} active · {inactive.length} inactive</span>}
      </div>

      {!selectedBrand ? <EmptyState message="Select a brand to manage its models" />
        : !models.length ? <EmptyState message="No models added for this brand" />
        : (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Model Name</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {models.map((m, i) => (
                    <tr key={m.id} style={{ opacity: m.active ? 1 : 0.55 }}>
                      <td style={{ color:'var(--text2)', fontSize:12 }}>{i+1}</td>
                      <td style={{ fontWeight: m.active ? 500 : 400 }}>{m.name}</td>
                      <td><StatusBadge active={m.active} /></td>
                      <td><ActionButtons item={m} onEdit={openEdit} onToggle={toggleActive} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:400 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Model' : 'Add Model'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            {!editing && (
              <div className="form-group">
                <label>Brand *</label>
                <select value={form.brand_id} onChange={e => setForm(f => ({ ...f, brand_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Model Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Latitude 5520" />
            </div>
            {editing && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Model'}</button>
            </div>
          </div>
        </div>
      )}

      {csvModal && (
        <CSVUploadModal
          title="Upload Asset Models — CSV"
          sampleFile={{
            name: 'models_sample.csv',
            content: 'Brand,Model\nDell,Latitude 5520\nDell,Inspiron 15 3520\nHP,ProBook 440 G10\nHP,EliteBook 840\nLenovo,ThinkPad E14\n'
          }}
          columns={[
            { key:'brand', label:'Brand' },
            { key:'model', label:'Model Name' },
          ]}
          mapRow={raw => {
            const brand = raw.brand || raw.brand_name || '';
            const model = raw.model || raw.model_name || raw.name || '';
            if (!brand.trim() || !model.trim()) return null;
            return { brand: brand.trim(), model: model.trim() };
          }}
          onUpload={rows => api.post('/master/models/import', { models: rows }).then(r => r.data)}
          onDone={() => loadModels(selectedBrand)}
          onClose={() => setCsvModal(false)}
          formatHint={
            <div style={{ fontSize:12, lineHeight:1.7 }}>
              <strong>Two columns:</strong> Brand, Model (or "Brand Name", "Model Name")<br />
              The Brand must already exist in the <strong>Asset Brands</strong> tab.<br />
              Duplicate brand+model combinations are automatically skipped.
            </div>
          }
        />
      )}
    </div>
  );
}

// ─── Device Types Tab (with field builder) ─────────────────────────────────────

const FIELD_TYPES = [
  { value:'text',     label:'Text' },
  { value:'number',   label:'Number' },
  { value:'password', label:'Password' },
  { value:'textarea', label:'Long Text' },
];

function slugify(label) {
  return (label || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function DeviceTypesTab() {
  const [types,   setTypes]   = useState([]);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ name:'', config_required:true, port_based:false, port_count:0, fields:[] });
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  const load = async () => {
    try { const r = await api.get('/master/device-types'); setTypes(r.data); } catch(e) {}
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name:'', config_required:true, port_based:false, port_count:24, fields:[] });
    setEditing(null); setErr(''); setModal(true);
  };
  const openEdit = t => {
    setForm({
      name: t.name, config_required: t.config_required, port_based: t.port_based,
      port_count: t.port_count || 0, active: t.active,
      fields: Array.isArray(t.fields) ? t.fields.map(f => ({ ...f })) : []
    });
    setEditing(t); setErr(''); setModal(true);
  };

  // Field builder operations
  const addField    = () => setForm(f => ({ ...f, fields: [...f.fields, { key:'', label:'', type:'text', required:false }] }));
  const removeField = i  => setForm(f => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));
  const moveField   = (i, dir) => setForm(f => {
    const arr = [...f.fields]; const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, fields: arr };
  });
  const setFieldProp = (i, prop, val) => setForm(f => ({
    ...f,
    fields: f.fields.map((fl, idx) => {
      if (idx !== i) return fl;
      const next = { ...fl, [prop]: val };
      if (prop === 'label') next.key = slugify(val); // auto-derive key from label
      return next;
    })
  }));

  const save = async () => {
    if (!form.name.trim()) { setErr('Device type name is required'); return; }
    // Validate fields: every field needs a label
    for (const fl of form.fields) {
      if (!fl.label?.trim()) { setErr('Every config field needs a label'); return; }
    }
    // Ensure unique keys
    const keys = form.fields.map(f => f.key);
    if (new Set(keys).size !== keys.length) { setErr('Field labels must be unique'); return; }

    setBusy(true); setErr('');
    const payload = {
      name: form.name.trim(),
      config_required: form.config_required,
      port_based: form.port_based,
      port_count: form.port_based ? (parseInt(form.port_count) || 0) : 0,
      fields: form.config_required ? form.fields : [],
    };
    try {
      if (editing) {
        if (form.active !== undefined) payload.active = form.active;
        await api.put(`/master/device-types/${editing.id}`, payload);
      } else {
        await api.post('/master/device-types', payload);
      }
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async t => {
    try { await api.put(`/master/device-types/${t.id}`, { active: !t.active }); load(); } catch(e) {}
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16 }}>
        <p style={{ fontSize:13, color:'var(--text2)', margin:0 }}>
          Define device types and the configuration fields shown for each. No code changes needed for new types.
        </p>
        <button className="btn btn-primary" onClick={openNew}>+ Add Device Type</button>
      </div>

      {!types.length ? <EmptyState message="No device types defined" /> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Device Type</th><th>Config Required</th><th>Port Based</th><th>Ports</th><th>Config Fields</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {types.map(t => (
                  <tr key={t.id} style={{ opacity: t.active ? 1 : 0.55 }}>
                    <td style={{ fontWeight:500 }}>{t.name}</td>
                    <td>{t.config_required ? <span className="badge badge-completed">Yes</span> : <span className="badge badge-normal">No</span>}</td>
                    <td>{t.port_based ? <span className="badge badge-discuss">Yes</span> : <span style={{ color:'var(--text2)', fontSize:12 }}>No</span>}</td>
                    <td><span style={{ fontSize:12 }}>{t.port_based ? t.port_count : '—'}</span></td>
                    <td>
                      {(t.fields && t.fields.length)
                        ? <span style={{ fontSize:12, color:'var(--text2)' }}>{t.fields.map(f => f.label).join(', ')}</span>
                        : <span style={{ fontSize:12, color:'var(--text2)' }}>—</span>}
                    </td>
                    <td><StatusBadge active={t.active} /></td>
                    <td>
                      <div className="flex-gap" style={{ gap:6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(t)}>Edit</button>
                        <button className={`btn btn-sm ${t.active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(t)}>
                          {t.active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:680 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? `Edit Device Type — ${editing.name}` : 'Add Device Type'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Device Type Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Firewall, NAS, UPS" />
              </div>
              {editing && (
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                    <option value="active">Active</option><option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>

            {/* Toggles */}
            <div style={{ display:'flex', gap:24, marginBottom:16, padding:'12px 14px', background:'var(--bg3)', borderRadius:'var(--radius)', border:'1px solid var(--border)' }}>
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:13 }}>
                <input type="checkbox" checked={form.config_required} onChange={e => setForm(f => ({ ...f, config_required: e.target.checked }))} style={{ width:15, height:15 }} />
                Configuration Required
              </label>
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:13 }}>
                <input type="checkbox" checked={form.port_based} onChange={e => setForm(f => ({ ...f, port_based: e.target.checked }))} style={{ width:15, height:15 }} />
                Port Based
              </label>
              {form.port_based && (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:13, color:'var(--text2)' }}>Number of Ports:</span>
                  <input type="number" min={1} max={256} value={form.port_count}
                    onChange={e => setForm(f => ({ ...f, port_count: e.target.value }))}
                    style={{ width:80, padding:'4px 8px' }} />
                </div>
              )}
            </div>

            {/* Field builder */}
            {form.config_required && (
              <div style={{ marginBottom:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <label style={{ margin:0 }}>Configuration Fields</label>
                  <button className="btn btn-secondary btn-sm" onClick={addField}>+ Add Field</button>
                </div>

                {form.fields.length === 0 ? (
                  <div style={{ fontSize:12, color:'var(--text2)', padding:'12px', textAlign:'center', background:'var(--bg3)', borderRadius:'var(--radius)', border:'1px dashed var(--border)' }}>
                    No fields yet. Click "+ Add Field" to define what this device type captures.
                  </div>
                ) : (
                  <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
                    {/* Header */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 110px 70px 90px', gap:8, padding:'8px 12px', background:'var(--bg3)', fontSize:11, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                      <span>Field Label</span><span>Type</span><span style={{ textAlign:'center' }}>Required</span><span style={{ textAlign:'center' }}>Order</span>
                    </div>
                    {form.fields.map((fl, i) => (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 110px 70px 90px', gap:8, padding:'8px 12px', alignItems:'center', borderTop:'1px solid var(--border)' }}>
                        <div>
                          <input value={fl.label} onChange={e => setFieldProp(i, 'label', e.target.value)} placeholder="e.g. IP Address" style={{ padding:'5px 8px', fontSize:13, width:'100%' }} />
                          {fl.key && <span className="mono" style={{ fontSize:10, color:'var(--text2)' }}>{fl.key}</span>}
                        </div>
                        <select value={fl.type} onChange={e => setFieldProp(i, 'type', e.target.value)} style={{ fontSize:12, padding:'5px 6px' }}>
                          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <div style={{ textAlign:'center' }}>
                          <input type="checkbox" checked={!!fl.required} onChange={e => setFieldProp(i, 'required', e.target.checked)} style={{ width:15, height:15 }} />
                        </div>
                        <div style={{ display:'flex', gap:2, justifyContent:'center' }}>
                          <button className="btn btn-secondary btn-sm" style={{ padding:'2px 6px' }} onClick={() => moveField(i, -1)} disabled={i === 0}>↑</button>
                          <button className="btn btn-secondary btn-sm" style={{ padding:'2px 6px' }} onClick={() => moveField(i, 1)} disabled={i === form.fields.length - 1}>↓</button>
                          <button className="btn btn-danger btn-sm" style={{ padding:'2px 6px' }} onClick={() => removeField(i)}>×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Device Type'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Class Teachers Tab (admin approvals) ──────────────────────────────────────
function ClassTeachersTab({ campuses }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassign, setReassign] = useState(null);   // mapping being reassigned
  const [rooms, setRooms] = useState([]);
  const [newRoom, setNewRoom] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/master/class-teachers'); setRows(r.data); } catch(e) {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, body) => { await api.put(`/master/class-teachers/${id}`, body); load(); };
  const del = async id => { if (!window.confirm('Remove this mapping?')) return; await api.delete(`/master/class-teachers/${id}`); load(); };

  const openReassign = async m => {
    setReassign(m); setNewRoom('');
    const r = await api.get(`/master/rooms/${m.campus_id}`); setRooms(r.data.filter(x => x.active));
  };
  const saveReassign = async () => {
    if (!newRoom) return;
    setBusy(true);
    try { await api.put(`/master/class-teachers/${reassign.id}`, { room_id: parseInt(newRoom) }); setReassign(null); load(); }
    finally { setBusy(false); }
  };

  const badge = s => <span className={`badge ${s==='Approved'?'badge-completed':s==='Rejected'?'badge-faulty':'badge-pending'}`}>{s}</span>;
  const pending = rows.filter(r => r.status === 'Pending');

  return (
    <div>
      <p style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
        Approve class-teacher requests. Approved teachers see their classroom's assets in the staff portal.
        {pending.length > 0 && <strong style={{ color:'var(--amber)' }}> · {pending.length} pending</strong>}
      </p>
      {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
      : !rows.length ? <EmptyState message="No class-teacher requests yet." />
      : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Staff</th><th>Campus</th><th>Room</th><th>Status</th><th>Reviewed By</th><th>Action</th></tr></thead>
              <tbody>
                {rows.map(m => (
                  <tr key={m.id}>
                    <td><div style={{ fontWeight:500 }}>{m.staff_name}</div><div style={{ fontSize:11, color:'var(--text2)' }}>{m.staff_email}</div></td>
                    <td style={{ fontSize:13 }}>{m.campus_name}</td>
                    <td style={{ fontSize:13 }}>{m.room_name || '—'}</td>
                    <td>{badge(m.status)}</td>
                    <td style={{ fontSize:12, color:'var(--text2)' }}>{m.reviewed_by_name || '—'}</td>
                    <td>
                      <div className="flex-gap" style={{ gap:6, flexWrap:'wrap' }}>
                        {m.status !== 'Approved' && <button className="btn btn-primary btn-sm" onClick={() => act(m.id, { status:'Approved' })}>Approve</button>}
                        {m.status === 'Pending' && <button className="btn btn-danger btn-sm" onClick={() => act(m.id, { status:'Rejected' })}>Reject</button>}
                        {m.status === 'Approved' && <button className="btn btn-secondary btn-sm" onClick={() => openReassign(m)}>Reassign</button>}
                        <button className="btn btn-secondary btn-sm" onClick={() => del(m.id)}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reassign && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setReassign(null)}>
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-header"><span className="modal-title">Reassign — {reassign.staff_name}</span><button className="modal-close" onClick={() => setReassign(null)}>×</button></div>
            <div className="form-group">
              <label>New Room ({reassign.campus_name})</label>
              <select value={newRoom} onChange={e => setNewRoom(e.target.value)}>
                <option value="">— Select room —</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReassign(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveReassign} disabled={busy || !newRoom}>{busy?'Saving…':'Reassign'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── IT Support Resources (Phase 3) ────────────────────────────────────────────
function ITResourcesTab({ campuses, isAdmin = true }) {
  const [sub, setSub] = useState('intercom');
  // Escalation matrix & IT contacts stay admin-only; IT staff maintain the intercom directory.
  const subs = isAdmin
    ? [['intercom','☎️ Intercom Directory'],['escalation','🚦 Escalation Matrix'],['contacts','👨‍💻 IT Contacts']]
    : [['intercom','☎️ Intercom Directory']];
  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {subs.map(([id,label]) => (
          <button key={id} onClick={() => setSub(id)}
            className={`btn btn-sm ${sub===id?'btn-primary':'btn-secondary'}`}>{label}</button>
        ))}
      </div>
      {sub === 'intercom'   && <ResourceCrud campuses={campuses} apiBase="/master/intercom" scoped
        fields={[{key:'label',label:'Label',required:true,placeholder:'e.g. Reception, Principal'},{key:'number',label:'Extension / Number',placeholder:'e.g. 101'},{key:'sort_order',label:'Order',type:'number',placeholder:'0'}]}
        cols={[{key:'campus_name',label:'Campus'},{key:'label',label:'Label'},{key:'number',label:'Number'},{key:'sort_order',label:'Order'}]} itemLabel="Intercom entry"
        csv={{
          title:'Upload Intercom Directory — CSV',
          sampleFile:{ name:'intercom_sample.csv', content:'campus,label,number,order\nCBE,Reception,101,1\nCBE,Principal,102,2\n' },
          columns:[{key:'campus',label:'Campus'},{key:'label',label:'Label'},{key:'number',label:'Number'},{key:'sort_order',label:'Order'}],
          mapRow:r => (r.label && r.label.trim()) ? { campus:(r.campus||'').trim(), label:r.label.trim(), number:(r.number||'').trim(), sort_order:r.order||r.sort_order||0 } : null,
          formatHint:<span>Columns: <b>campus</b> (code or name), <b>label</b>, <b>number</b>, <b>order</b>. Campus &amp; label are required.</span>,
        }} />}
      {sub === 'escalation' && (
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>Common Levels (org-wide)</div>
          <ResourceCrud apiBase="/master/escalation"
            fields={[{key:'level',label:'Level',type:'number',required:true,placeholder:'1'},{key:'role_title',label:'Role Title',placeholder:'e.g. IT Manager'}]}
            cols={[{key:'level',label:'Level'},{key:'role_title',label:'Role Title'}]} itemLabel="Escalation level" />
          <div style={{ height:24 }} />
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>Assign IT Person per Campus</div>
          <EscalationCampusMap campuses={campuses} />
        </div>
      )}
      {sub === 'contacts'   && <ResourceCrud campuses={campuses} apiBase="/master/it-contacts" scoped
        fields={[{key:'name',label:'Name',required:true},{key:'designation',label:'Designation',placeholder:'e.g. IT Support Executive'},{key:'phone',label:'Phone'},{key:'email',label:'Email',type:'email'}]}
        cols={[{key:'campus_name',label:'Campus'},{key:'name',label:'Name'},{key:'designation',label:'Designation'},{key:'phone',label:'Phone'},{key:'email',label:'Email'}]} itemLabel="IT contact" />}
    </div>
  );
}

// Campus-wise escalation: pick which IT contact fills each common level for a campus
function EscalationCampusMap({ campuses }) {
  const [campusId, setCampusId] = useState(campuses && campuses.length === 1 ? String(campuses[0].id) : '');
  const [levels, setLevels] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [saved, setSaved] = useState(null);

  const load = useCallback(async () => {
    if (!campusId) { setLevels([]); setContacts([]); return; }
    try {
      const [lv, ct] = await Promise.all([
        api.get('/master/escalation-campus', { params:{ campus_id: campusId } }),
        api.get('/master/it-contacts', { params:{} }),
      ]);
      setLevels(lv.data);
      setContacts(ct.data.filter(c => String(c.campus_id) === String(campusId)));
    } catch(e) {}
  }, [campusId]);
  useEffect(() => { load(); }, [load]);

  const setPerson = async (level, contact_id) => {
    await api.put('/master/escalation-campus', { campus_id: parseInt(campusId), level, contact_id: contact_id ? parseInt(contact_id) : null });
    setSaved(level); setTimeout(() => setSaved(null), 1200);
    load();
  };

  return (
    <div className="card">
      <div className="form-group" style={{ maxWidth:320 }}>
        <label>Campus</label>
        <select value={campusId} onChange={e => setCampusId(e.target.value)}>
          <option value="">— Select campus —</option>
          {(campuses||[]).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      {!campusId ? <div style={{ fontSize:13, color:'var(--text2)' }}>Select a campus to assign its escalation contacts.</div>
      : !levels.length ? <div style={{ fontSize:13, color:'var(--text2)' }}>No common levels defined yet — add them above.</div>
      : (
        <div className="table-wrap"><table>
          <thead><tr><th>Level</th><th>Role</th><th>IT Person (this campus)</th></tr></thead>
          <tbody>
            {levels.map(l => (
              <tr key={l.level}>
                <td><span className="badge badge-pending">L{l.level}</span></td>
                <td style={{ fontSize:13 }}>{l.role_title || '—'}</td>
                <td>
                  <select value={l.contact_id || ''} onChange={e => setPerson(l.level, e.target.value)} style={{ minWidth:220 }}>
                    <option value="">— Not assigned —</option>
                    {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.designation ? ` (${c.designation})` : ''}</option>)}
                  </select>
                  {saved === l.level && <span style={{ fontSize:12, color:'var(--green)', marginLeft:8 }}>✓ saved</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {campusId && !contacts.length && <div style={{ fontSize:11, color:'var(--amber)', marginTop:8 }}>No IT contacts for this campus yet — add them under the "IT Contacts" sub-tab first.</div>}
    </div>
  );
}

// Generic CRUD list for IT resource tables (optionally campus-scoped)
function ResourceCrud({ apiBase, fields, cols, itemLabel, campuses, scoped, csv, extra }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [csvModal, setCsvModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = () => { const f = {}; fields.forEach(x => f[x.key] = x.type==='checkboxes' ? [] : ''); if (scoped) f.campus_id = (campuses && campuses.length===1) ? String(campuses[0].id) : ''; return f; };
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(apiBase); setRows(r.data); } catch(e) {}
    setLoading(false);
  }, [apiBase]);
  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setEditing(null); setForm(blank()); setErr(''); setModal(true); };
  const openEdit = r => { const f = {}; fields.forEach(x => f[x.key] = r[x.key] ?? (x.type==='checkboxes' ? [] : '')); if (scoped) f.campus_id = String(r.campus_id||''); setEditing(r); setForm(f); setErr(''); setModal(true); };
  const del = async r => { if (!window.confirm(`Delete this ${itemLabel}?`)) return; await api.delete(`${apiBase}/${r.id}`); load(); };

  const save = async () => {
    if (scoped && !form.campus_id) { setErr('Select a campus'); return; }
    setBusy(true); setErr('');
    const body = { ...form };
    if (scoped) body.campus_id = parseInt(form.campus_id);
    try {
      if (editing) await api.put(`${apiBase}/${editing.id}`, body);
      else await api.post(apiBase, body);
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'flex-end', gap:8, marginBottom:12 }}>
        {typeof extra === 'function' ? extra(load) : extra}
        {csv && <button className="btn btn-secondary btn-sm" onClick={() => setCsvModal(true)}>⬆ Upload CSV</button>}
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add {itemLabel}</button>
      </div>
      {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
      : !rows.length ? <EmptyState message={`No ${itemLabel.toLowerCase()}s yet.`} />
      : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}<th></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  {cols.map(c => <td key={c.key} style={{ fontSize:13 }}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                  <td><div className="flex-gap" style={{ gap:6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => del(r)}>Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:460 }}>
            <div className="modal-header"><span className="modal-title">{editing?'Edit':'Add'} {itemLabel}</span><button className="modal-close" onClick={() => setModal(false)}>×</button></div>
            {scoped && (
              <div className="form-group">
                <label>Campus *</label>
                <select value={form.campus_id} onChange={e => setForm(f => ({ ...f, campus_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {(campuses||[]).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {fields.map(fl => (
              <div className="form-group" key={fl.key}>
                <label>{fl.label}{fl.required?' *':''}</label>
                {fl.type === 'checkboxes'
                  ? <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 14px' }}>
                      {(fl.options||[]).map(o => {
                        const arr = Array.isArray(form[fl.key]) ? form[fl.key] : [];
                        const on = arr.includes(o.value);
                        return (
                          <label className="opt" key={o.value} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13 }}>
                            <input type="checkbox" checked={on} onChange={() =>
                              setForm(f => ({ ...f, [fl.key]: on ? arr.filter(x => x!==o.value) : [...arr, o.value] }))} />
                            {o.label}
                          </label>
                        );
                      })}
                    </div>
                  : fl.type === 'select'
                  ? <select value={String(form[fl.key] ?? '')} onChange={e => setForm(f => ({ ...f, [fl.key]: e.target.value }))}>
                      {(fl.options||[]).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  : <input type={fl.type||'text'} value={form[fl.key]} placeholder={fl.placeholder||''}
                      onChange={e => setForm(f => ({ ...f, [fl.key]: e.target.value }))} />}
                {fl.hint && <span style={{ fontSize:11, color:'var(--text2)' }}>{fl.hint}</span>}
              </div>
            ))}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {csv && csvModal && (
        <CSVUploadModal
          title={csv.title}
          sampleFile={csv.sampleFile}
          columns={csv.columns}
          mapRow={csv.mapRow}
          formatHint={csv.formatHint}
          onUpload={rows => api.post(`${apiBase}/import`, { records: rows }).then(r => r.data)}
          onDone={load}
          onClose={() => setCsvModal(false)}
        />
      )}
    </div>
  );
}

// ─── Accessories (Phase 4) ──────────────────────────────────────────────────
function AccessoriesTab() {
  const [sub, setSub] = useState('assignments');
  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {[['assignments','📦 Assignments'],['types','🏷 Accessory Types']].map(([id,label]) => (
          <button key={id} onClick={() => setSub(id)} className={`btn btn-sm ${sub===id?'btn-primary':'btn-secondary'}`}>{label}</button>
        ))}
      </div>
      {sub === 'types' && <ResourceCrud apiBase="/master/accessory-types"
        fields={[{key:'name',label:'Accessory Name',required:true,placeholder:'e.g. Headset, Webcam, USB Adapter'}]}
        cols={[{key:'name',label:'Accessory Type'}]} itemLabel="Accessory type" />}
      {sub === 'assignments' && <AccessoryAssignments />}
    </div>
  );
}

function AccessoryAssignments() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [csvModal, setCsvModal] = useState(false);
  const [form, setForm] = useState({ user_id:'', accessory_type_id:'', serial_number:'', notes:'', assigned_date:'' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/master/accessories'); setRows(r.data); } catch(e) {}
    setLoading(false);
  }, []);
  useEffect(() => {
    load();
    api.get('/master/accessory-types').then(r => setTypes(r.data)).catch(()=>{});
    api.get('/master/users').then(r => setUsers(r.data)).catch(()=>{});
  }, [load]);

  const openNew = () => { setForm({ user_id:'', accessory_type_id:'', serial_number:'', notes:'', assigned_date:'' }); setErr(''); setModal(true); };
  const save = async () => {
    if (!form.user_id) { setErr('Select a staff member'); return; }
    if (!form.accessory_type_id) { setErr('Select an accessory type'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/master/accessories', { ...form, user_id:parseInt(form.user_id), accessory_type_id:parseInt(form.accessory_type_id), assigned_date: form.assigned_date || undefined });
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  const markReturned = async r => { if (!window.confirm(`Mark ${r.accessory_name} returned by ${r.staff_name}?`)) return; await api.put(`/master/accessories/${r.id}`, { serial_number:r.serial_number, notes:r.notes, status:'Returned' }); load(); };
  const del = async r => { if (!window.confirm('Delete this record?')) return; await api.delete(`/master/accessories/${r.id}`); load(); };

  const badge = s => <span className={`badge ${s==='Returned'?'badge-discussed':'badge-completed'}`}>{s}</span>;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:12 }}>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Assign accessories to staff. Items appear in the staff member's Profile while issued. Items without a serial number (projector remote, stylus…) auto-get an asset code from <b>Master Data → Asset Codes</b>.</p>
        <div className="flex-gap" style={{ gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setCsvModal(true)}>⬆ Bulk Upload</button>
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ Assign Accessory</button>
        </div>
      </div>
      {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
      : !rows.length ? <EmptyState message="No accessories assigned yet." />
      : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Staff</th><th>Accessory</th><th>Serial No.</th><th>Asset Code</th><th>Assigned</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td><div style={{ fontWeight:500 }}>{r.staff_name}</div><div style={{ fontSize:11, color:'var(--text2)' }}>{r.staff_email}</div></td>
                  <td style={{ fontSize:13 }}>{r.accessory_name || '—'}{r.notes && <div style={{ fontSize:11, color:'var(--text2)' }}>{r.notes}</div>}</td>
                  <td style={{ fontSize:12 }} className="mono">{r.serial_number || '—'}</td>
                  <td style={{ fontSize:12 }} className="mono">{r.asset_code ? <span style={{ color:'var(--accent)' }}>{r.asset_code}</span> : '—'}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{r.assigned_date ? new Date(r.assigned_date).toLocaleDateString() : '—'}</td>
                  <td>{badge(r.status)}{r.status==='Returned' && r.returned_date && <div style={{ fontSize:11, color:'var(--text2)' }}>{new Date(r.returned_date).toLocaleDateString()}</div>}</td>
                  <td><div className="flex-gap" style={{ gap:6 }}>
                    {r.status==='Issued' && <button className="btn btn-secondary btn-sm" onClick={() => markReturned(r)}>Mark Returned</button>}
                    <button className="btn btn-danger btn-sm" onClick={() => del(r)}>Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:460 }}>
            <div className="modal-header"><span className="modal-title">Assign Accessory</span><button className="modal-close" onClick={() => setModal(false)}>×</button></div>
            <div className="form-group">
              <label>Staff Member *</label>
              <select value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id:e.target.value }))}>
                <option value="">— Select staff —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Accessory Type *</label>
              <select value={form.accessory_type_id} onChange={e => setForm(f => ({ ...f, accessory_type_id:e.target.value }))}>
                <option value="">— Select accessory —</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {!types.length && <div style={{ fontSize:11, color:'var(--amber)', marginTop:4 }}>No accessory types yet — add some under "Accessory Types".</div>}
            </div>
            <div className="form-row">
              <div className="form-group"><label>Serial Number</label><input value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number:e.target.value }))} /></div>
              <div className="form-group"><label>Assigned Date</label><input type="date" value={form.assigned_date} onChange={e => setForm(f => ({ ...f, assigned_date:e.target.value }))} /></div>
            </div>
            <div className="form-group"><label>Notes</label><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes:e.target.value }))} placeholder="optional" /></div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Assign'}</button>
            </div>
          </div>
        </div>
      )}

      {csvModal && (
        <CSVUploadModal
          title="Bulk Assign Accessories — CSV"
          sampleFile={{ name:'accessories_sample.csv', content:'email,accessory,campus,assigned_date\nteacher1@school.com,Projector Remote,CBE,\nteacher2@school.com,Stylus,CBE,\n' }}
          columns={[{key:'email',label:'Staff Email'},{key:'accessory',label:'Accessory'},{key:'campus',label:'Campus'},{key:'assigned_date',label:'Assigned'}]}
          mapRow={r => (r.email && r.email.trim() && r.accessory && r.accessory.trim())
            ? { email:r.email.trim(), accessory:r.accessory.trim(), campus:(r.campus||'').trim(), serial_number:(r.serial_number||'').trim(), assigned_date:(r.assigned_date||'').trim() || undefined }
            : null}
          formatHint={<span>Columns: <b>email</b> (staff login/email), <b>accessory</b> (e.g. Projector Remote, Stylus), <b>campus</b> (optional — used for the asset-code series), <b>assigned_date</b> (optional). Items with no serial number auto-get an asset code; configure the prefix under <b>Asset Codes</b> (kind = Accessory).</span>}
          onUpload={rows => api.post('/master/accessories/import', { records: rows }).then(r => r.data)}
          onDone={load}
          onClose={() => setCsvModal(false)}
        />
      )}
    </div>
  );
}

// ─── Asset Codes (campus + device type → tag prefix) ───────────────────────────
function AssetCodesTab({ campuses }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [accTypes, setAccTypes] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => { api.get('/master/asset-codes').then(r => setRows(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    load();
    api.get('/master/device-types').then(r => setTypes(r.data)).catch(()=>{});
    api.get('/master/accessory-types').then(r => setAccTypes(r.data)).catch(()=>{});
  }, [load]);

  const openNew  = () => { setModal({ campus_id: campuses[0] ? String(campuses[0].id) : '', asset_type:'', code:'', kind:'device' }); setErr(''); };
  const openEdit = r => { setModal({ id:r.id, campus_id:String(r.campus_id), asset_type:r.asset_type, code:r.code, kind:r.kind||'device' }); setErr(''); };
  const save = async () => {
    if (!modal.campus_id || !modal.asset_type || !modal.code.trim()) { setErr('All fields are required'); return; }
    setBusy(true); setErr('');
    try {
      if (modal.id) await api.put(`/master/asset-codes/${modal.id}`, { code: modal.code });
      else await api.post('/master/asset-codes', { campus_id: parseInt(modal.campus_id), asset_type: modal.asset_type, code: modal.code, kind: modal.kind });
      setModal(null); load();
    } catch(e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  const del = async r => { if (window.confirm(`Delete asset code "${r.code}"?`)) { await api.delete(`/master/asset-codes/${r.id}`); load(); } };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:14 }}>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Define the asset-tag prefix per campus, for a <b>device type</b> or an <b>accessory</b> (projector remote, stylus…). New items auto-generate tags like <span className="mono">CODE-001</span>.</p>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add Asset Code</button>
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <EmptyState message="No asset codes yet. Assets fall back to CAMPUS-YEAR-SEQ until you add one." /> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Campus</th><th>Kind</th><th>Type</th><th>Code (prefix)</th><th>Active</th><th></th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontSize:13 }}>{r.campus_name}</td>
                <td style={{ fontSize:12 }}><span className={`badge ${r.kind==='accessory'?'badge-discussed':'badge-completed'}`}>{r.kind==='accessory'?'Accessory':'Device'}</span></td>
                <td style={{ fontSize:13 }}>{r.asset_type}</td>
                <td className="mono" style={{ fontSize:13, color:'var(--accent)' }}>{r.code}</td>
                <td style={{ fontSize:12 }}>{r.active ? '✓' : '—'}</td>
                <td><div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => del(r)}>Delete</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:440 }}>
            <div className="modal-header"><span className="modal-title">{modal.id?'Edit':'Add'} Asset Code</span><button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-group"><label>Campus *</label>
              <select value={modal.campus_id} disabled={!!modal.id} onChange={e => setModal(m => ({ ...m, campus_id:e.target.value }))}>
                <option value="">— Select —</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label>Kind *</label>
              <select value={modal.kind} disabled={!!modal.id} onChange={e => setModal(m => ({ ...m, kind:e.target.value, asset_type:'' }))}>
                <option value="device">Device Type</option><option value="accessory">Accessory</option>
              </select></div>
            <div className="form-group"><label>{modal.kind==='accessory'?'Accessory':'Device Type'} *</label>
              <select value={modal.asset_type} disabled={!!modal.id} onChange={e => setModal(m => ({ ...m, asset_type:e.target.value }))}>
                <option value="">— Select —</option>
                {(modal.kind==='accessory'?accTypes:types).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
              {modal.kind==='accessory' && !accTypes.length && <div style={{ fontSize:11, color:'var(--amber)', marginTop:4 }}>No accessory types yet — add them under Accessories → Accessory Types.</div>}
            </div>
            <div className="form-group"><label>Code (prefix) *</label>
              <input value={modal.code} onChange={e => setModal(m => ({ ...m, code:e.target.value.toUpperCase() }))} placeholder="e.g. CBE-FW" />
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>Tags will be generated as <span className="mono">{(modal.code||'CODE')}-001</span>, <span className="mono">{(modal.code||'CODE')}-002</span>, …</div>
            </div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Master Page ──────────────────────────────────────────────────────────────

// Holidays drive the working-day task age: a ticket does not age on a Sunday
// or on a day the campus was closed. A holiday with no campus applies to all.
function HolidaysTab({ campuses, isAdmin }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [csv, setCsv] = useState(false);
  const [f, setF] = useState({ holiday_date:'', name:'', campus_id:'' });
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    api.get('/master/holidays', { params:{ year } }).then(r => setRows(r.data)).catch(()=>{});
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f.holiday_date || !f.name.trim()) { setErr('Date and name are required'); return; }
    try {
      await api.post('/master/holidays', { ...f, campus_id: f.campus_id || null });
      setAdding(false); setF({ holiday_date:'', name:'', campus_id:'' }); setErr(''); load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
  };
  const del = async r => {
    if (!window.confirm(`Remove "${r.name}" on ${r.holiday_date}?`)) return;
    await api.delete(`/master/holidays/${r.id}`).catch(()=>{});
    load();
  };

  const years = [thisYear - 1, thisYear, thisYear + 1];

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
        <div>
          <b style={{ fontSize:14 }}>Holidays</b>
          <div style={{ fontSize:11.5, color:'var(--text2)' }}>
            Task age skips Sundays automatically. Add the days your campuses are closed so they are not counted either.
          </div>
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ width:110 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => setCsv(true)}>⬆ Bulk Upload</button>}
          {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => { setAdding(true); setErr(''); }}>+ Add Holiday</button>}
        </div>
      </div>

      {adding && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="form-row">
            <div className="form-group"><label>Date *</label>
              <input type="date" value={f.holiday_date} onChange={e => setF(s => ({ ...s, holiday_date:e.target.value }))} /></div>
            <div className="form-group"><label>Name *</label>
              <input value={f.name} onChange={e => setF(s => ({ ...s, name:e.target.value }))} placeholder="e.g. Pongal" /></div>
            <div className="form-group"><label>Campus</label>
              <select value={f.campus_id} onChange={e => setF(s => ({ ...s, campus_id:e.target.value }))}>
                <option value="">All campuses</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
          </div>
          {err && <div className="err-msg">{err}</div>}
          <div className="flex-gap" style={{ gap:8 }}>
            <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setAdding(false); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap"><table>
          <thead><tr><th>Date</th><th>Day</th><th>Holiday</th><th>Applies to</th><th>Source</th>{isAdmin && <th></th>}</tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={isAdmin?6:5} style={{ padding:16, color:'var(--text2)', fontSize:13 }}>
              No holidays listed for {year}. Sundays are already excluded from task age.</td></tr>}
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight:500 }}>{r.holiday_date}</td>
                <td style={{ fontSize:12, color: r.weekday === 'Sun' ? 'var(--text2)' : 'var(--text)' }}>
                  {r.weekday}{r.weekday === 'Sun' ? ' (already off)' : ''}</td>
                <td>{r.name}</td>
                <td style={{ fontSize:12 }}>{r.campus_name || 'All campuses'}</td>
                <td style={{ fontSize:11.5, color:'var(--text2)' }}>{r.source}</td>
                {isAdmin && <td><button className="btn btn-danger btn-sm" onClick={() => del(r)}>Remove</button></td>}
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {csv && (
        <CSVUploadModal
          title="Bulk Upload Holidays"
          columns={[{key:'holiday_date',label:'Date'},{key:'name',label:'Holiday'},{key:'campus',label:'Campus'}]}
          // A blank campus means every campus is closed; a named one means only
          // that campus — the sample shows both.
          sampleFile={{
            name: 'holidays_sample.csv',
            content: 'Date,Holiday,Campus\r\n'
              + '2027-01-01,New Year\'s Day,\r\n'
              + '2027-01-14,Pongal,\r\n'
              + '2027-01-26,Republic Day,\r\n'
              + '2027-04-14,Local Festival,Chennai Main\r\n',
          }}
          formatHint={<><strong>Required:</strong> Date (2026-01-26), Holiday name. <strong>Optional:</strong> Campus
            — leave blank when every campus is closed; set it for a holiday only one state observes.</>}
          mapRow={raw => {
            const get = (...keys) => { for (const k of keys) { const hit = Object.keys(raw).find(h => h.trim().toLowerCase() === k); if (hit && String(raw[hit]).trim()) return String(raw[hit]).trim(); } return ''; };
            const date = get('date','holiday_date','holiday date');
            const name = get('holiday','name','holiday name','occasion','description');
            if (!date || !name) return null;
            return { holiday_date: date, name, campus: get('campus','campus name') };
          }}
          onUpload={rows => api.post('/master/holidays/import', { records: rows }).then(r => r.data)}
          onDone={load}
          onClose={() => setCsv(false)}
        />
      )}
    </div>
  );
}

const TABS = [
  { id:'campus',  label:'Campus' },
  { id:'rooms',   label:'Rooms' },
  { id:'locations',label:'Locations' },
  { id:'staff',   label:'Staff Details' },
  { id:'students',label:'Student Details' },
  { id:'classteachers', label:'Class Teachers' },
  { id:'brands',  label:'Asset Brands' },
  { id:'models',  label:'Asset Models' },
  { id:'devicetypes', label:'Device Types' },
  { id:'assetcodes',  label:'Asset Codes' },
  { id:'groups',      label:'Group IDs' },
  { id:'gwous',       label:'Google OU Mapping' },
  { id:'gwgroups',    label:'Google Groups' },
  { id:'categories',  label:'Task Categories' },
  { id:'meetingtypes', label:'Meeting Types' },
  { id:'welcomeart',   label:'Welcome Images' },
  { id:'holidays',    label:'Holidays' },
  { id:'sop',         label:'SOP Templates' },
  { id:'vendors',     label:'Vendors' },
  { id:'incidenttypes', label:'Incident Types' },
  { id:'itresources', label:'IT Resources' },
  { id:'accessories', label:'Accessories' },
];

// ── Load groups from Google ─────────────────────────────────────────────────
// Google groups belong to the domain, not to a campus — the portal's Group IDs
// master is campus-scoped. So this lists what Google has and asks which campus
// each chosen group belongs to; nothing is imported until that is answered.
function GoogleGroupsImport({ campuses, onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [existing, setExisting] = useState([]);
  const [picked, setPicked] = useState({});        // group_email -> campus_id
  const [bulkCampus, setBulkCampus] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');

  const openModal = async () => {
    setOpen(true); setBusy(true); setMsg(''); setRows(null);
    try {
      const [g, cur] = await Promise.all([
        api.get('/gw/groups'),
        api.get('/master/groups').catch(() => ({ data: [] })),
      ]);
      setRows(g.data);
      setExisting(cur.data || []);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Could not read the groups from Google');
      setRows([]);
    } finally { setBusy(false); }
  };

  // A group already in the master for some campus is shown but not offered
  // again, so a second import cannot create a duplicate.
  const alreadyIn = email =>
    existing.filter(x => (x.group_email || '').toLowerCase() === email.toLowerCase());

  const save = async () => {
    const records = Object.entries(picked)
      .filter(([, campusId]) => campusId)
      .map(([email, campusId]) => {
        const g = rows.find(r => r.email === email);
        const c = campuses.find(x => String(x.id) === String(campusId));
        return { campus: c?.name, name: g?.name || email.split('@')[0], group_email: email };
      });
    if (!records.length) { setMsg('Choose a campus for at least one group'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await api.post('/master/groups/import', { records });
      const d = r.data;
      setMsg(`✓ ${d.inserted} added, ${d.updated} updated${d.errors?.length ? ` · ${d.errors.length} problem(s)` : ''}`);
      onDone && onDone();
      setTimeout(() => { setOpen(false); setPicked({}); setMsg(''); }, 1600);
    } catch (e) { setMsg(e.response?.data?.error || 'Could not import'); }
    finally { setBusy(false); }
  };

  const visible = (rows || []).filter(g =>
    !q || g.email.toLowerCase().includes(q.toLowerCase()) || (g.name || '').toLowerCase().includes(q.toLowerCase()));
  const chosen = Object.values(picked).filter(Boolean).length;

  return (<>
    <button className="btn btn-secondary btn-sm" onClick={openModal}>↓ Load groups from Google</button>
    {open && (
      <div className="modal-backdrop" onClick={() => !busy && setOpen(false)}>
        <div className="modal" style={{ maxWidth:860 }} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginTop:0 }}>Groups in Google Workspace</h3>
          <p style={{ fontSize:12.5, color:'var(--text2)', marginTop:0 }}>
            Google groups belong to the whole domain, so choose which campus each one should sit under
            in the portal. Only the groups you give a campus are imported.
          </p>

          {rows === null ? <div style={{ padding:20, color:'var(--text2)' }}>Reading from Google…</div>
          : !rows.length ? <div style={{ padding:16, color:'var(--red)' }}>{msg || 'No groups returned.'}</div>
          : (<>
            <div className="flex-gap" style={{ gap:8, marginBottom:10, flexWrap:'wrap', alignItems:'end' }}>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter…" style={{ flex:1, minWidth:160 }} />
              <div>
                <label style={{ fontSize:11, color:'var(--text2)' }}>Set campus for all shown</label>
                <select value={bulkCampus} onChange={e => {
                  setBulkCampus(e.target.value);
                  if (!e.target.value) return;
                  setPicked(p => {
                    const next = { ...p };
                    visible.forEach(g => { if (!alreadyIn(g.email).length) next[g.email] = e.target.value; });
                    return next;
                  });
                }}>
                  <option value="">— choose —</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => { setPicked({}); setBulkCampus(''); }}>Clear</button>
            </div>

            <div className="table-wrap" style={{ maxHeight:360, overflowY:'auto' }}><table>
              <thead><tr><th>Group</th><th style={{ width:90 }}>Members</th><th style={{ width:220 }}>Campus in portal</th></tr></thead>
              <tbody>{visible.map(g => {
                const have = alreadyIn(g.email);
                return (
                  <tr key={g.email} style={{ opacity: have.length ? .55 : 1 }}>
                    <td style={{ fontSize:12.5 }}>
                      <b>{g.name || '—'}</b>
                      <div className="mono" style={{ color:'var(--text2)', fontSize:11.5 }}>{g.email}</div>
                    </td>
                    <td style={{ fontSize:12, color:'var(--text2)' }}>{g.members ?? '—'}</td>
                    <td>
                      {have.length
                        ? <span className="badge badge-completed">already in {have.map(h => h.campus_name).join(', ')}</span>
                        : <select value={picked[g.email] || ''}
                            onChange={e => setPicked(p => ({ ...p, [g.email]: e.target.value }))}>
                            <option value="">— not imported —</option>
                            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
            <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:6 }}>
              {visible.length} of {rows.length} groups shown · {chosen} selected for import
            </div>
          </>)}

          {msg && <div style={{ fontSize:13, marginTop:10, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}
          <div className="flex-gap" style={{ gap:8, justifyContent:'flex-end', marginTop:12 }}>
            <button className="btn btn-secondary" onClick={() => setOpen(false)} disabled={busy}>Close</button>
            <button className="btn btn-primary" onClick={save} disabled={busy || !chosen}>
              {busy ? '…' : `Import ${chosen || ''}`}
            </button>
          </div>
        </div>
      </div>
    )}
  </>);
}

// ── Verify one staff member's Google placement ──────────────────────────────
// Reads Google and compares it with the OU and groups recorded here. Passing
// hides the button for good; failing keeps it, with the reason shown, because
// the accounts that fail are the whole point of the exercise.
function GwVerifyCell({ person: p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // this session's run

  const verify = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/gw/verify-staff/${p.id}`);
      setResult(r.data);
      if (r.data.ok) onChanged();               // refresh so the button goes
    } catch (e) {
      setResult({ ok:false, problems:[e.response?.data?.error || 'Check failed'] });
    } finally { setBusy(false); }
  };

  const fix = async (what) => {
    const label = what === 'ou' ? 'move them to the correct organizational unit'
                                : 'add them to the groups they are missing';
    if (!window.confirm(`This will ${label} in Google, for real.\n\nContinue?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/gw/fix-staff/${p.id}`, { what });
      setResult({ ok:false, problems:[], fixed: r.data.done });
      await verify();                            // re-check so the state is true
    } catch (e) {
      setResult({ ok:false, problems:[e.response?.data?.error || 'Could not fix'] });
      setBusy(false);
    }
  };

  // Already passed — nothing to do, and no button.
  if (p.gw_verified_at && p.gw_verify_ok) {
    return <span style={{ color:'var(--green)' }}>
      ✓ verified
      <div style={{ color:'var(--text2)', fontSize:10.5 }}>
        {new Date(p.gw_verified_at).toLocaleDateString('en-IN')}
        {p.gw_verified_by_name ? ` · ${p.gw_verified_by_name}` : ''}
      </div>
    </span>;
  }

  const shown = result || (p.gw_verify_note
    ? { ok:false, problems:[p.gw_verify_note], stale:true } : null);
  const wrongOu = shown && shown.problems?.some(x => /expected \//.test(x));
  const missingGroup = shown && shown.problems?.some(x => /^Not in /.test(x));

  return (
    <div>
      <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
              disabled={busy} onClick={verify}>{busy ? '…' : 'Verify'}</button>
      {shown && (
        <div style={{ marginTop:4 }}>
          {shown.fixed?.length > 0 && (
            <div style={{ color:'var(--green)', fontSize:10.5 }}>{shown.fixed.join(' · ')}</div>
          )}
          {shown.problems?.length > 0 && (
            <div style={{ color:'var(--red)', fontSize:10.5, lineHeight:1.35 }}>
              {shown.stale && <span style={{ color:'var(--text2)' }}>last check: </span>}
              {shown.problems.join(' · ')}
            </div>
          )}
          {(wrongOu || missingGroup) && (
            <div className="flex-gap" style={{ gap:4, marginTop:3, flexWrap:'wrap' }}>
              {wrongOu && <button className="btn btn-secondary" style={{ padding:'1px 6px', fontSize:10 }}
                disabled={busy} onClick={() => fix('ou')}>fix OU</button>}
              {missingGroup && <button className="btn btn-secondary" style={{ padding:'1px 6px', fontSize:10 }}
                disabled={busy} onClick={() => fix('groups')}>add groups</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Bulk OU assignment ──────────────────────────────────────────────────────
// Mapping 18 campuses one dialog at a time is the slow part, so this is the
// same shape as the group import: filter, set a campus for everything shown,
// adjust the odd row, save once.
function BulkOuAssign({ campuses, mapped, onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});       // ou_path -> { campus_id, name }
  const [bulkCampus, setBulkCampus] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const openModal = async () => {
    setOpen(true); setMsg(''); setRows(null); setPicked({});
    try { setRows((await api.get('/gw/directory')).data.org_units); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not read the cached directory'); setRows([]); }
  };

  // Already mapped, so not offered again — a second run cannot duplicate.
  const already = p => mapped.find(m => m.ou_path === p);
  // The last segment is the obvious unit name; editable per row.
  const defaultName = p => p.split('/').filter(Boolean).pop() || 'Default';

  const visible = (rows || []).filter(o => !q || o.ou_path.toLowerCase().includes(q.toLowerCase()));
  const chosen = Object.values(picked).filter(x => x && x.campus_id).length;

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api.post('/gw/ous/bulk', {
        rows: Object.entries(picked).filter(([, v]) => v && v.campus_id)
          .map(([ou_path, v]) => ({ ou_path, campus_id: v.campus_id, name: v.name })),
      });
      const d = r.data;
      setMsg(`✓ ${d.created} added, ${d.updated} updated`
        + (d.errors?.length ? ` · ${d.errors.length} problem(s): ${d.errors[0]}` : ''));
      onDone && onDone();
      if (!d.errors?.length) setTimeout(() => { setOpen(false); setPicked({}); setMsg(''); }, 1600);
    } catch (e) { setMsg(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };

  return (<>
    <button className="btn btn-secondary btn-sm" onClick={openModal}>⇄ Assign OUs in bulk</button>
    {open && (
      <div className="modal-backdrop" onClick={() => !busy && setOpen(false)}>
        <div className="modal" style={{ maxWidth:900 }} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginTop:0 }}>Assign organizational units to campuses</h3>
          <p style={{ fontSize:12.5, color:'var(--text2)', marginTop:0 }}>
            From the cached directory — no Google call. Filter to a campus's units, set the campus
            for everything shown, then save.
          </p>
          {rows === null ? <div style={{ padding:20, color:'var(--text2)' }}>Loading…</div>
          : !rows.length ? <div style={{ padding:16, color:'var(--red)' }}>
              {msg || 'No organizational units cached — sync the directory first.'}</div>
          : (<>
            <div className="flex-gap" style={{ gap:8, marginBottom:10, flexWrap:'wrap', alignItems:'end' }}>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by path…"
                     style={{ flex:1, minWidth:180 }} />
              <div>
                <label style={{ fontSize:11, color:'var(--text2)' }}>Set campus for all shown</label>
                <select value={bulkCampus} onChange={e => {
                  setBulkCampus(e.target.value);
                  if (!e.target.value) return;
                  setPicked(p => {
                    const next = { ...p };
                    visible.forEach(o => {
                      if (already(o.ou_path)) return;
                      next[o.ou_path] = { campus_id: e.target.value,
                                          name: (next[o.ou_path]?.name) || o.name || defaultName(o.ou_path) };
                    });
                    return next;
                  });
                }}>
                  <option value="">— choose —</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => { setPicked({}); setBulkCampus(''); }}>Clear</button>
            </div>

            <div className="table-wrap" style={{ maxHeight:380, overflowY:'auto' }}><table>
              <thead><tr><th>OU path</th><th style={{ width:180 }}>Unit name</th><th style={{ width:200 }}>Campus</th></tr></thead>
              <tbody>{visible.map(o => {
                const have = already(o.ou_path);
                const sel = picked[o.ou_path] || {};
                return (
                  <tr key={o.ou_path} style={{ opacity: have ? .55 : 1 }}>
                    <td className="mono" style={{ fontSize:12 }}>{o.ou_path}
                      {!o.present && <span className="badge badge-high" style={{ marginLeft:6, fontSize:10 }}>not in Google</span>}</td>
                    <td>{have ? <span style={{ fontSize:12.5 }}>{have.name}</span>
                      : <input value={sel.name ?? (o.name || defaultName(o.ou_path))} style={{ fontSize:12 }}
                               onChange={e => setPicked(p => ({ ...p, [o.ou_path]: { ...(p[o.ou_path]||{}), name:e.target.value } }))} />}</td>
                    <td>{have
                      ? <span className="badge badge-completed">already {have.campus_name}</span>
                      : <select value={sel.campus_id || ''}
                          onChange={e => setPicked(p => ({ ...p, [o.ou_path]: {
                            name: p[o.ou_path]?.name ?? (o.name || defaultName(o.ou_path)),
                            campus_id: e.target.value } }))}>
                          <option value="">— not mapped —</option>
                          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>}</td>
                  </tr>
                );
              })}</tbody>
            </table></div>
            <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:6 }}>
              {visible.length} of {rows.length} shown · {chosen} selected.
              The first unit mapped to a campus becomes its default unless you set another.
            </div>
          </>)}
          {msg && <div style={{ fontSize:13, marginTop:10, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}
          <div className="flex-gap" style={{ gap:8, justifyContent:'flex-end', marginTop:12 }}>
            <button className="btn btn-secondary" onClick={() => setOpen(false)} disabled={busy}>Close</button>
            <button className="btn btn-primary" onClick={save} disabled={busy || !chosen}>
              {busy ? '…' : `Assign ${chosen || ''}`}</button>
          </div>
        </div>
      </div>
    )}
  </>);
}

// ── Google Groups & members ─────────────────────────────────────────────────
// The groups Google has, from the cached directory, and who is in each one.
// Reading is open to anyone who can see this screen; adding or removing takes
// the "Add / remove Google group members" permission, because it changes real
// access rather than a record in the portal.
function GoogleGroupsTab({ isAdmin }) {
  const { can } = useAuth();
  const [groups, setGroups] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [sel, setSel] = useState(null);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState('');
  const [newMember, setNewMember] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const mayChange = can('gw_members', 'view');

  const load = useCallback(() => {
    api.get('/gw/directory').then(r => { setGroups(r.data.groups); setSyncedAt(r.data.synced_at); })
       .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openGroup = async (g) => {
    setSel(g); setMembers([]); setMsg('');
    try { setMembers((await api.get(`/gw/groups/${encodeURIComponent(g.group_email)}/members`)).data); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not read the members'); }
  };

  const syncMembers = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api.post(`/gw/groups/${encodeURIComponent(sel.group_email)}/sync`);
      setMsg(`✓ ${r.data.members} member(s) read from Google`);
      openGroup(sel); load();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not sync from Google'); }
    finally { setBusy(false); }
  };

  const add = async () => {
    setBusy(true); setMsg('');
    try {
      await api.post(`/gw/groups/${encodeURIComponent(sel.group_email)}/members`, { email: newMember.trim() });
      setNewMember(''); openGroup(sel);
    } catch (e) { setMsg(e.response?.data?.error || 'Could not add'); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.member_email} from ${sel.group_email}?\n\nThis takes away their access in Google, not just here.`)) return;
    setBusy(true); setMsg('');
    try {
      await api.delete(`/gw/groups/${encodeURIComponent(sel.group_email)}/members/${encodeURIComponent(m.member_email)}`);
      openGroup(sel);
    } catch (e) { setMsg(e.response?.data?.error || 'Could not remove'); }
    finally { setBusy(false); }
  };

  const shown = groups.filter(g => !q
    || g.group_email.toLowerCase().includes(q.toLowerCase())
    || (g.name || '').toLowerCase().includes(q.toLowerCase()));

  if (sel) return (
    <div>
      <button className="btn btn-secondary" style={{ marginBottom:12 }} onClick={() => { setSel(null); setMsg(''); }}>
        ← All groups
      </button>
      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:15 }}>{sel.name || sel.group_email}</b>
        <div className="mono" style={{ fontSize:12.5, color:'var(--text2)' }}>{sel.group_email}</div>
        {!sel.present && <div style={{ fontSize:12, color:'var(--red)', marginTop:4 }}>
          This group was not returned by the last sync — it may have been deleted in Google.
        </div>}
        <div className="flex-gap" style={{ gap:8, marginTop:10, flexWrap:'wrap' }}>
          {isAdmin && <button className="btn btn-secondary" disabled={busy} onClick={syncMembers}>
            {busy ? '…' : '↻ Read members from Google'}</button>}
        </div>
        {msg && <div style={{ fontSize:12.5, marginTop:8, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}
      </div>

      {mayChange && (
        <div className="card" style={{ marginBottom:14, padding:12 }}>
          <div className="flex-gap" style={{ gap:8, alignItems:'end' }}>
            <div className="form-group" style={{ margin:0, flex:1 }}>
              <label>Add a member</label>
              <input value={newMember} onChange={e => setNewMember(e.target.value)}
                     placeholder="name@tipsglobeducate.com" />
            </div>
            <button className="btn btn-primary" disabled={busy || !newMember.trim()} onClick={add}>Add</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!members.length
          ? <div className="empty-state"><p>
              No members recorded. {isAdmin ? 'Use “Read members from Google” to pull the current list.' : ''}
            </p></div>
          : <div className="table-wrap"><table>
              <thead><tr><th>Address</th><th>Known as</th><th>Role</th>{mayChange && <th></th>}</tr></thead>
              <tbody>{members.map(m => (
                <tr key={m.member_email}>
                  <td className="mono" style={{ fontSize:12.5 }}>{m.member_email}</td>
                  <td style={{ fontSize:12.5 }}>{m.portal_name || m.staff_name
                    || <span style={{ color:'var(--text3)' }}>not a portal or staff record</span>}</td>
                  <td><span className="badge badge-normal">{m.role || 'MEMBER'}</span></td>
                  {mayChange && <td>
                    <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                            disabled={busy} onClick={() => remove(m)}>remove</button>
                  </td>}
                </tr>
              ))}</tbody>
            </table></div>}
      </div>
    </div>
  );

  return (
    <div>
      <p style={{ fontSize:13, color:'var(--text2)', marginBottom:12 }}>
        The groups in your Google Workspace, from the last directory sync
        {syncedAt ? ` (${new Date(syncedAt).toLocaleString('en-IN')})` : ' — not synced yet'}.
        Sync it from <b>Google OU Mapping</b>.
      </p>
      <div className="form-group" style={{ maxWidth:320 }}>
        <label>Filter</label>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="name or address" />
      </div>
      {!shown.length ? <div className="empty-state"><p>No groups cached yet.</p></div>
      : <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Group</th><th>Address</th><th>Members</th><th></th></tr></thead>
            <tbody>{shown.map(g => (
              <tr key={g.group_email} style={{ cursor:'pointer', opacity: g.present ? 1 : .55 }}
                  onClick={() => openGroup(g)}>
                <td style={{ fontSize:13 }}>{g.name || '—'}
                  {!g.present && <span className="badge badge-high" style={{ marginLeft:6, fontSize:10 }}>not in Google</span>}</td>
                <td className="mono" style={{ fontSize:12 }}>{g.group_email}</td>
                <td style={{ fontSize:12.5 }}>{g.member_count ?? '—'}</td>
                <td style={{ fontSize:12, color:'var(--accent)' }}>members →</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>}
    </div>
  );
}

// ── Google OU Mapping ───────────────────────────────────────────────────────
// A campus has several Organizational Units — Admin, Academic, PA & PE — and
// a new account goes into one of them. This is the only place that mapping
// lives; an unmapped campus deliberately blocks account creation rather than
// guessing, because the wrong OU is worse than a clear stop.
function GoogleOuTab({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [googleOus, setGoogleOus] = useState(null);
  const [f, setF] = useState({ campus_id:'', name:'', ou_path:'', is_default:false });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/gw/ous').then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => {
    load();
    api.get('/master/campuses').then(r => setCampuses(r.data)).catch(()=>{});
  }, []);

  const add = async () => {
    if (!f.campus_id || !f.name.trim() || !f.ou_path.trim()) { setMsg('Campus, name and OU path are all needed'); return; }
    setBusy(true); setMsg('');
    try { await api.post('/gw/ous', f); setF({ campus_id:f.campus_id, name:'', ou_path:'', is_default:false }); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };
  const upd = async (id, patch) => {
    try { await api.put(`/gw/ous/${id}`, patch); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not update'); }
  };
  const del = async (o) => {
    if (!window.confirm(`Remove "${o.name}" for ${o.campus_name}?`)) return;
    try { const r = await api.delete(`/gw/ous/${o.id}`);
      if (r.data.deactivated) setMsg('In use by an onboarding record, so it was deactivated rather than deleted.');
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not remove'); }
  };
  // The cached directory — written by a sync, read here. Loading it needs no
  // Google call, so the dropdown is populated on every visit rather than only
  // after someone remembers to press a button.
  const [syncedAt, setSyncedAt] = useState(null);
  const loadCached = useCallback(() => {
    api.get('/gw/directory').then(r => {
      setGoogleOus(r.data.org_units.length ? r.data.org_units.map(o => ({ path:o.ou_path, name:o.name, present:o.present })) : null);
      setSyncedAt(r.data.synced_at);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadCached(); }, [loadCached]);

  // Refreshes the cache from Google. Done once, not once per campus.
  const pullOus = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api.post('/gw/sync-directory');
      const gone = r.data.no_longer_in_google || {};
      setMsg(`✓ ${r.data.org_units} organizational units and ${r.data.groups} groups synced`
        + (gone.org_units ? ` · ${gone.org_units} OU(s) no longer in Google` : ''));
      loadCached();
    }
    catch (e) { setMsg(e.response?.data?.error || 'Could not read the directory from Google'); }
    finally { setBusy(false); }
  };

  const byCampus = {};
  rows.forEach(o => { (byCampus[o.campus_name] = byCampus[o.campus_name] || []).push(o); });
  const unmapped = campuses.filter(c => !rows.some(o => o.campus_id === c.id && o.active));

  return (
    <div>
      <p style={{ fontSize:13, color:'var(--text2)', marginBottom:12 }}>
        Where a new staff account is created in Google. Each campus can have several units —
        Admin, Academic, PA&nbsp;&amp;&nbsp;PE — and one is the default.
      </p>

      {isAdmin && (
        <div className="card" style={{ marginBottom:14, padding:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1.3fr 1fr 1.6fr auto auto', gap:10, alignItems:'end' }}>
            <div><label>Campus</label>
              <select value={f.campus_id} onChange={e => setF(s=>({...s,campus_id:e.target.value}))}>
                <option value="">— select —</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div><label>Unit name</label>
              <input value={f.name} onChange={e => setF(s=>({...s,name:e.target.value}))} placeholder="Academic" /></div>
            <div><label>OU path</label>
              {googleOus
                ? <select value={f.ou_path} onChange={e => setF(s=>({...s,ou_path:e.target.value}))}>
                    <option value="">— choose —</option>
                    {googleOus.map(o => <option key={o.path} value={o.path}>{o.path}</option>)}
                  </select>
                : <input value={f.ou_path} onChange={e => setF(s=>({...s,ou_path:e.target.value}))} placeholder="/Campuses/CBE-CBSE/Academic" />}</div>
            <label className="opt"><input type="checkbox" checked={f.is_default}
              onChange={e => setF(s=>({...s,is_default:e.target.checked}))} /><span>Default</span></label>
            <button className="btn btn-primary" onClick={add} disabled={busy}>Add</button>
          </div>
          <div style={{ marginTop:8 }}>
            <button className="btn btn-secondary" onClick={pullOus} disabled={busy}>
              {busy ? 'Syncing…' : '↻ Sync directory from Google'}
            </button>
            {' '}
            <BulkOuAssign campuses={campuses} mapped={rows} onDone={load} />
            <span style={{ fontSize:11, color:'var(--text2)', marginLeft:8 }}>
              {syncedAt
                ? <>Organizational units and groups were last synced {new Date(syncedAt).toLocaleString('en-IN')}
                    {googleOus ? ` · ${googleOus.length} OU(s) available to map` : ''}. They are stored here,
                    so you only sync when Google changes — not each time you map a campus.</>
                : <>Not synced yet. One sync stores every OU and group here, and the mapping
                    dropdowns read from that — no need to load again for each campus.</>}
            </span>
          </div>
        </div>
      )}

      {msg && <div style={{ fontSize:12.5, color:'var(--red)', marginBottom:10 }}>{msg}</div>}

      {unmapped.length > 0 && (
        <div className="card" style={{ marginBottom:14, padding:10, borderColor:'var(--red)' }}>
          <b style={{ fontSize:13 }}>Not mapped yet — accounts cannot be created for these campuses:</b>
          <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:4 }}>
            {unmapped.map(c => c.name).join(', ')}
          </div>
        </div>
      )}

      {!rows.length ? <div className="empty-state"><p>No organizational units mapped yet.</p></div>
      : Object.entries(byCampus).map(([campus, list]) => (
        <div className="card" key={campus} style={{ marginBottom:12, padding:12 }}>
          <b style={{ fontSize:14 }}>{campus}</b>
          <div className="table-wrap" style={{ marginTop:8 }}><table>
            <thead><tr><th>Unit</th><th>OU path</th><th style={{ width:90 }}>Default</th>
              <th style={{ width:80 }}>Active</th>{isAdmin && <th style={{ width:80 }}></th>}</tr></thead>
            <tbody>{list.map(o => (
              <tr key={o.id} style={{ opacity:o.active?1:.5 }}>
                <td style={{ fontSize:13 }}>{o.name}</td>
                <td className="mono" style={{ fontSize:12.5 }}>{o.ou_path}</td>
                <td>{o.is_default
                  ? <span className="badge badge-completed">default</span>
                  : isAdmin && <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                      onClick={() => upd(o.id, { is_default:true })}>make default</button>}</td>
                <td>{isAdmin
                  ? <label className="opt"><input type="checkbox" checked={o.active}
                      onChange={e => upd(o.id, { active:e.target.checked })} /><span /></label>
                  : (o.active ? 'yes' : 'no')}</td>
                {isAdmin && <td><button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                  onClick={() => del(o)}>remove</button></td>}
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      ))}
    </div>
  );
}

// ── Welcome Images ──────────────────────────────────────────────────────────
// Your own artwork for the scene IT staff see when they sign in. Each picture
// is pinned to a time of day and a mood; anything you leave empty keeps the
// drawn scene, which is also what shows if a file ever goes missing.
const WI_TIMES = [['any', 'Any time'], ['morning', 'Morning'], ['evening', 'Evening']];
const WI_MOODS = [
  ['any',      'Any mood',            'the catch-all'],
  ['clear',    'All clear',           'nothing outstanding'],
  ['steady',   'Steady',              '1–5 open'],
  ['busy',     'Busy',                '6–12 open'],
  ['swamped',  'Swamped',             '13 or more open'],
  ['incident', 'Incident open',       'a critical call is live'],
  ['streak',   'Clean week (reward)', 'not awarded yet'],
];

function WelcomeImagesTab({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ part_of_day: 'any', mood: 'any', caption: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    api.get('/master/welcome-images').then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); };

  const upload = async () => {
    if (!file) { flash('Choose an image first', false); return; }
    if (file.size > 4 * 1024 * 1024) {
      flash('That image is over 4 MB. It loads on sign-in, so keep it small.', false); return;
    }
    setBusy(true);
    try {
      const data = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result));
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      await api.post('/master/welcome-images', {
        orig_name: file.name, mime: file.type, data, ...f,
      });
      flash('Uploaded. It shows at the next sign-in for that slot.');
      setFile(null); setF(s => ({ ...s, caption: '' }));
      load();
    } catch (e) { flash(e.response?.data?.error || 'Upload failed', false); }
    finally { setBusy(false); }
  };

  const toggle = async r => {
    try { await api.put(`/master/welcome-images/${r.id}`, { active: !r.active }); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not update', false); }
  };

  const remove = async r => {
    if (!window.confirm(`Delete "${r.orig_name}"?\n\nThe drawn scene takes its place.`)) return;
    try { await api.delete(`/master/welcome-images/${r.id}`); flash('Deleted'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not delete', false); }
  };

  const label = (list, v) => (list.find(x => x[0] === v) || [v, v])[1];

  return (
    <div>
      {msg && (
        <div className={msg.ok ? '' : 'err-msg'}
             style={msg.ok ? { color:'var(--green)', fontSize:13, marginBottom:10 } : { marginBottom:10 }}>
          {msg.text}
        </div>
      )}

      <div className="card" style={{ marginBottom:14, fontSize:13, color:'var(--text2)' }}>
        IT staff see a short welcome once per sign-in. It is drawn in the browser by default —
        no file needed. Upload your own picture here to take its place for a particular time of
        day and workload. GIF, PNG, JPEG, WebP or SVG, up to 4&nbsp;MB.
      </div>

      {isAdmin && (
        <div className="card" style={{ marginBottom:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:12 }}>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Time of day</label>
              <select value={f.part_of_day} onChange={e => setF(s => ({ ...s, part_of_day: e.target.value }))}>
                {WI_TIMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Mood</label>
              <select value={f.mood} onChange={e => setF(s => ({ ...s, mood: e.target.value }))}>
                {WI_MOODS.map(([v, l, note]) => <option key={v} value={v}>{l} — {note}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Note to yourself</label>
              <input value={f.caption} onChange={e => setF(s => ({ ...s, caption: e.target.value }))}
                placeholder="e.g. Diwali greeting" />
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Image</label>
              <input type="file" accept="image/gif,image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <div style={{ marginTop:12, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <button className="btn btn-primary" onClick={upload} disabled={busy || !file}>
              {busy ? 'Uploading…' : 'Upload'}
            </button>
            {file && (
              <span style={{ fontSize:12.5, color:'var(--text2)' }}>
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </span>
            )}
            <span style={{ fontSize:12, color:'var(--text2)' }}>
              A picture for a specific slot beats a general one. 300×190 is the drawn size.
            </span>
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:14 }}>
        {!rows.length && (
          <div className="card" style={{ color:'var(--text2)', fontSize:13.5 }}>
            Nothing uploaded. Every slot uses the drawn scene.
          </div>
        )}
        {rows.map(r => (
          <div key={r.id} className="card" style={{ padding:0, overflow:'hidden',
                opacity: r.active ? 1 : .55 }}>
            <img src={`/api/master/welcome-images/${r.id}/file`} alt={r.orig_name}
              style={{ display:'block', width:'100%', height:150, objectFit:'cover',
                       background:'var(--bg3)' }} />
            <div style={{ padding:'12px 14px 14px' }}>
              <div style={{ fontSize:13.5, fontWeight:600 }}>
                {label(WI_TIMES, r.part_of_day)} · {label(WI_MOODS, r.mood)}
              </div>
              <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:2 }}>
                {r.orig_name} · {(r.size_bytes / 1024).toFixed(0)} KB · {r.uploaded_on}
              </div>
              {r.caption && (
                <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:4, fontStyle:'italic' }}>
                  {r.caption}
                </div>
              )}
              {isAdmin && (
                <div className="flex-gap" style={{ gap:8, marginTop:10 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => toggle(r)}>
                    {r.active ? 'Switch off' : 'Switch on'}
                  </button>
                  <button className="btn btn-secondary btn-sm" style={{ color:'var(--red)' }}
                    onClick={() => remove(r)}>Delete</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Meeting Types ───────────────────────────────────────────────────────────
// What kinds of meeting the portal offers — IT Head, IT Lead, and whatever
// else you run: a project meeting, a service-pending review. Adding one used
// to be a code change.
function MeetingTypesTab({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ name:'', description:'' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    api.get('/containers/meeting-types', { params:{ include:'all' } })
      .then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 3500); };

  const add = async () => {
    if (!f.name.trim()) return;
    setBusy(true);
    try {
      await api.post('/containers/meeting-types', f);
      setF({ name:'', description:'' }); flash('Meeting type added'); load();
    } catch (e) { flash(e.response?.data?.error || 'Could not add', false); }
    finally { setBusy(false); }
  };

  const toggle = async t => {
    try { await api.put(`/containers/meeting-types/${t.id}`, { active: !t.active }); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not update', false); }
  };

  const rename = async t => {
    const name = window.prompt('Meeting type name', t.name);
    if (!name || name === t.name) return;
    try { await api.put(`/containers/meeting-types/${t.id}`, { name }); flash('Renamed'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not rename', false); }
  };

  const remove = async t => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/containers/meeting-types/${t.id}`); flash('Deleted'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not delete', false); }
  };

  return (
    <div>
      {msg && (
        <div className={msg.ok ? '' : 'err-msg'}
             style={msg.ok ? { color:'var(--green)', fontSize:13, marginBottom:10 } : { marginBottom:10 }}>
          {msg.text}
        </div>
      )}

      {isAdmin && (
        <div className="card" style={{ marginBottom:14, display:'flex', gap:10,
                                       flexWrap:'wrap', alignItems:'flex-end' }}>
          <div className="form-group" style={{ marginBottom:0, flex:'1 1 220px' }}>
            <label>New meeting type</label>
            <input value={f.name} onChange={e => setF(s => ({ ...s, name:e.target.value }))}
              placeholder="e.g. Project Meeting" />
          </div>
          <div className="form-group" style={{ marginBottom:0, flex:'2 1 320px' }}>
            <label>Description</label>
            <input value={f.description} onChange={e => setF(s => ({ ...s, description:e.target.value }))}
              placeholder="e.g. Weekly review of running projects" />
          </div>
          <button className="btn btn-primary" onClick={add} disabled={busy || !f.name.trim()}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Meeting type</th><th>Description</th><th>Meetings</th><th>Status</th>
                  {isAdmin && <th></th>}</tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr><td colSpan={isAdmin ? 5 : 4} style={{ color:'var(--text2)', padding:16 }}>
                  No meeting types yet.
                </td></tr>
              )}
              {rows.map(t => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight:600 }}>{t.name}</div>
                    <div className="mono" style={{ fontSize:11, color:'var(--text2)' }}>{t.code}</div>
                  </td>
                  <td style={{ color:'var(--text2)', fontSize:13 }}>{t.description || '—'}</td>
                  <td className="mono">{t.meeting_count}</td>
                  <td>
                    <span style={{ fontSize:12, fontWeight:600,
                                   color: t.active ? 'var(--green)' : 'var(--text2)' }}>
                      {t.active ? 'Active' : 'Off'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td style={{ whiteSpace:'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => rename(t)}>Rename</button>{' '}
                      <button className="btn btn-secondary btn-sm" onClick={() => toggle(t)}>
                        {t.active ? 'Switch off' : 'Switch on'}
                      </button>{' '}
                      {!t.meeting_count && (
                        <button className="btn btn-secondary btn-sm" style={{ color:'var(--red)' }}
                          onClick={() => remove(t)}>Delete</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize:12, color:'var(--text2)', marginTop:10 }}>
        Switching a type off removes it from the “new meeting” dropdown; meetings already
        recorded against it are untouched. A type nothing has used can be deleted outright.
      </div>
    </div>
  );
}

// ── SOP Templates (Module 5): admin-editable mandatory checklists ───────────
function SopTemplatesTab() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [nt, setNt] = useState({ name:'', category_id:'' });
  const [openId, setOpenId] = useState(null);
  const [item, setItem] = useState('');
  const [msg, setMsg] = useState('');
  const load = () => api.get('/master/sop-templates').then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => { load(); api.get('/master/categories').then(r => setCats(r.data.filter(c=>c.active!==false))).catch(()=>{}); }, []);
  const flash = m => { setMsg(m); setTimeout(()=>setMsg(''),2500); };
  const addTemplate = async () => {
    if (!nt.name.trim()) { flash('Name required'); return; }
    try { await api.post('/master/sop-templates', { name:nt.name.trim(), category_id:nt.category_id||null }); setNt({ name:'', category_id:'' }); load(); }
    catch(e){ flash('Failed'); }
  };
  const updTemplate = async (id, patch) => { try { await api.put(`/master/sop-templates/${id}`, patch); load(); } catch(e){ flash('Failed'); } };
  const delTemplate = async id => { if (window.confirm('Delete this SOP template and its steps?')) { await api.delete(`/master/sop-templates/${id}`); load(); } };
  const addItem = async id => { if (!item.trim()) return; await api.post(`/master/sop-templates/${id}/items`, { label:item.trim() }); setItem(''); load(); };
  const updItem = async (iid, patch) => { await api.put(`/master/sop-items/${iid}`, patch); load(); };
  const delItem = async iid => { await api.delete(`/master/sop-items/${iid}`); load(); };

  return (
    <div>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
        A task in a category linked here must have every <b>required</b> step ticked before it can be completed.
      </div>
      <div className="card" style={{ marginBottom:14, display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div className="form-group" style={{ flex:2, minWidth:180, margin:0 }}><label>New template</label>
          <input value={nt.name} onChange={e=>setNt(s=>({...s,name:e.target.value}))} placeholder="e.g. Laptop Allocation" /></div>
        <div className="form-group" style={{ flex:1, minWidth:160, margin:0 }}><label>Applies to category</label>
          <select value={nt.category_id} onChange={e=>setNt(s=>({...s,category_id:e.target.value}))}>
            <option value="">— none —</option>{cats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <button className="btn btn-primary" onClick={addTemplate}>+ Add Template</button>
      </div>
      {msg && <div style={{ fontSize:13, marginBottom:8, color:'var(--red)' }}>{msg}</div>}

      {!rows.length ? <EmptyState message="No SOP templates yet" /> : rows.map(t => (
        <div key={t.id} className="card" style={{ marginBottom:10, opacity:t.active?1:0.6 }}>
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <div>
              <b>{t.name}</b>
              <span style={{ fontSize:12, color:'var(--text2)', marginLeft:8 }}>
                {t.category_name ? `→ ${t.category_name}` : 'no category link'} · {t.items.length} step(s)
              </span>
            </div>
            <div className="flex-gap" style={{ gap:6 }}>
              <label className="opt" style={{ fontSize:12, display:'flex', gap:4, alignItems:'center' }}>
                <input type="checkbox" checked={t.active} onChange={e=>updTemplate(t.id,{active:e.target.checked})} /> Active</label>
              <select value={t.category_id||''} onChange={e=>updTemplate(t.id,{category_id:e.target.value||null})} style={{ fontSize:12, padding:'3px 5px' }}>
                <option value="">— category —</option>{cats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select>
              <button className="btn btn-secondary btn-sm" onClick={()=>setOpenId(openId===t.id?null:t.id)}>{openId===t.id?'Hide':'Steps'}</button>
              <button className="btn btn-danger btn-sm" onClick={()=>delTemplate(t.id)}>Delete</button>
            </div>
          </div>
          {openId === t.id && (
            <div style={{ marginTop:10, borderTop:'1px solid var(--border)', paddingTop:10 }}>
              {t.items.map(i => (
                <div key={i.id} className="flex-gap" style={{ gap:8, alignItems:'center', marginBottom:5 }}>
                  <input value={i.label} onChange={e=>updItem(i.id,{label:e.target.value})} style={{ flex:1, padding:'4px 6px' }} />
                  <label className="opt" style={{ fontSize:12, display:'flex', gap:4, alignItems:'center' }}>
                    <input type="checkbox" checked={i.required} onChange={e=>updItem(i.id,{required:e.target.checked})} /> Required</label>
                  <button className="btn btn-danger btn-sm" onClick={()=>delItem(i.id)}>×</button>
                </div>
              ))}
              <div className="flex-gap" style={{ gap:8, marginTop:6 }}>
                <input value={openId===t.id?item:''} onChange={e=>setItem(e.target.value)} placeholder="New step label" style={{ flex:1, padding:'4px 6px' }}
                  onKeyDown={e=>{ if(e.key==='Enter') addItem(t.id); }} />
                <button className="btn btn-primary btn-sm" onClick={()=>addItem(t.id)}>+ Add step</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Master({ initialTab }) {
  const { isAdmin, user } = useAuth();
  // IT staff/head get this page too — the day-to-day master data they maintain.
  const isIT = ['admin','it_staff','it_head','it_lead'].includes(user?.role);
  // IT Admins manage the day-to-day operational lookups too (not campuses,
  // SOP templates or vendors — those stay admin-only).
  const IT_TABS = ['staff','students','groups','itresources',
    'rooms','locations','categories','brands','models','devicetypes','assetcodes','accessories'];
  // A menu item can deep-link straight to a tab (e.g. Staff / Student).
  const [tab,      setTab]      = useState(initialTab || (isAdmin ? 'campus' : 'staff'));
  // Clicking a staff name opens their profile in place of the list.
  const [profileId, setProfileId] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [roles,    setRoles]    = useState([]);

  useEffect(() => {
    if (!isIT) return;
    api.get('/master/campuses').then(r => setCampuses(r.data)).catch(() => {});
    if (isAdmin) api.get('/master/roles').then(r => setRoles(r.data.filter(r => r.active))).catch(() => {});
  }, [isIT, isAdmin]);

  if (!isIT) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;
  const visibleTabs = isAdmin ? TABS : TABS.filter(t => IT_TABS.includes(t.id));

  // Category visibility uses the actual roles defined under the Roles menu
  const roleOptions = roles.map(r => ({ value:String(r.id), label:r.name }));
  const roleNames = r => {
    const v = r.visible_roles || [];
    if (!v.length) return 'Everyone';
    return v.map(id => roleOptions.find(o => o.value === String(id))?.label || id).join(', ');
  };

  // Config for campus-scoped tabs
  const ROOMS_FIELDS = [
    { key:'code', label:'Room Code', placeholder:'e.g. LAB3', upper:true, maxLength:20 },
    { key:'name', label:'Room Name', required:true, placeholder:'e.g. Computer Lab 3' },
  ];
  const ROOMS_COLS = [
    { key:'code', label:'Code', mono:true },
    { key:'name', label:'Room Name' },
  ];

  const LOC_FIELDS = [
    { key:'name', label:'Location Name', required:true, placeholder:'e.g. First Floor, Block B' },
  ];
  const LOC_COLS = [{ key:'name', label:'Location Name' }];

  const IB_FIELDS = [
    { key:'name',        label:'Full Name',    required:true, placeholder:'e.g. Rajesh Kumar' },
    { key:'designation', label:'Designation',  placeholder:'e.g. Principal' },
    { key:'email',       label:'Email',        type:'email', placeholder:'e.g. rajesh@school.edu' },
  ];
  const IB_COLS = [
    { key:'name',        label:'Name' },
    { key:'designation', label:'Designation', muted:true },
    { key:'email',       label:'Email',       muted:true },
  ];

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Master Data</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>
          {isAdmin ? 'Manage campuses, rooms, locations, contacts, and asset catalogue'
                   : 'Staff, students, group IDs and IT resources for your campuses'}
        </p>
      </div>

      {/* Tab bar */}
      {/* Wraps instead of scrolling — with this many tabs, ones on the right
          (e.g. Group IDs) were sliding off-screen and looked missing. */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:0, marginBottom:24, borderBottom:'1px solid var(--border)' }}>
        {visibleTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding:'9px 20px', fontSize:13, fontFamily:'var(--font)', cursor:'pointer',
              background:'transparent', border:'none', whiteSpace:'nowrap',
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text2)',
              fontWeight: tab === t.id ? 600 : 400,
              marginBottom:-1, transition:'all 0.15s'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'campus'     && <CampusTab />}
      {tab === 'rooms'      && (
        <CampusScopedTab campuses={campuses} apiBase="/master/rooms"
          columns={ROOMS_COLS} formFields={ROOMS_FIELDS} title="Rooms" itemLabel="Room" />
      )}
      {tab === 'locations'  && (
        <CampusScopedTab campuses={campuses} apiBase="/master/locations"
          columns={LOC_COLS} formFields={LOC_FIELDS} title="Locations" itemLabel="Location" />
      )}
      {tab === 'staff' && (
        profileId
          ? <StaffProfile staffId={profileId} onBack={() => setProfileId(null)} />
          : <StaffDetailsTab campuses={campuses.filter(c => c.active)} roles={roles}
              allCampuses={campuses.filter(c => c.active)} isAdmin={isAdmin}
              onOpenProfile={setProfileId} />
      )}
      {tab === 'students' && (
        <StudentsTab campuses={campuses.filter(c => c.active)} />
      )}
      {tab === 'classteachers' && (
        <ClassTeachersTab campuses={campuses.filter(c => c.active)} />
      )}
      {tab === 'brands'     && <BrandsTab />}
      {tab === 'models'     && <ModelsTab />}
      {tab === 'devicetypes'&& <DeviceTypesTab />}
      {tab === 'assetcodes' && <AssetCodesTab campuses={campuses.filter(c => c.active)} />}
      {tab === 'incidenttypes' && <ResourceCrud apiBase="/master/incident-types" itemLabel="Incident Type"
        fields={[{key:'name',label:'Type Name',required:true,placeholder:'e.g. Physical Damage'},{key:'kind',label:'Capture Form',type:'select',options:[{value:'general',label:'General'},{value:'physical_damage',label:'Physical Damage (student + asset + room + CCTV)'},{value:'policy_violation',label:'Policy Violation (student + site/proxy)'}]}]}
        cols={[{key:'name',label:'Type'},{key:'kind',label:'Form'}]} />}
      {tab === 'groups' && <ResourceCrud apiBase="/master/groups" itemLabel="Group ID" scoped
        campuses={campuses.filter(c => c.active)}
        extra={isAdmin ? (reload =>
          <GoogleGroupsImport campuses={campuses.filter(c => c.active)} onDone={reload} />) : null}
        fields={[
          {key:'name',label:'Group Name',required:true,placeholder:'e.g. All Staff'},
          {key:'group_email',label:'Group ID / Email',type:'email',placeholder:'e.g. staff@tipsglobeducate.com'},
          {key:'description',label:'Description',placeholder:'Who belongs in this group?'},
        ]}
        csv={{
          title:'Upload Group IDs — CSV',
          sampleFile:{ name:'group_ids_sample.csv',
            content:'campus,name,group_email,description\nCBE-CBSE,All Staff,cbe-cbse-staff@tipsglobeducate.com,Every staff member\nCBE-CBSE,Teaching,cbe-cbse-teaching@tipsglobeducate.com,Teaching staff only\n' },
          columns:[{key:'campus',label:'Campus'},{key:'name',label:'Group Name'},
                   {key:'group_email',label:'Group ID / Email'},{key:'description',label:'Description'}],
          // A row with no group name is nothing to import; everything else is
          // validated on the server, which knows the campus list.
          mapRow:r => (r.name && r.name.trim())
            ? { campus:(r.campus||'').trim(), name:r.name.trim(),
                group_email:(r.group_email||r.group_id||r.email||'').trim(),
                description:(r.description||'').trim() }
            : null,
          formatHint:<span>Columns: <b>campus</b> (code or name), <b>name</b>, <b>group_email</b>, <b>description</b>.
            Campus and name are required. A group that already exists for that campus is updated, not duplicated.</span>,
        }}
        cols={[
          {key:'campus_name',label:'Campus'},
          {key:'name',label:'Group'},
          {key:'group_email',label:'Group ID'},
          {key:'description',label:'Description'},
        ]} />}
      {tab === 'categories' && <ResourceCrud apiBase="/master/categories" itemLabel="Category"
        fields={[
          {key:'name',label:'Category Name',required:true,placeholder:'e.g. Laptop'},
          {key:'category_type',label:'Applies to',type:'select',options:[{value:'staff',label:'Staff tickets'},{value:'student',label:'Student tickets'},{value:'both',label:'Both'}]},
          {key:'visible_roles',label:'Visible to roles',type:'checkboxes',hint:'Leave all unchecked = visible to everyone.',
            options:roleOptions},
        ]}
        cols={[
          {key:'name',label:'Category'},
          {key:'category_type',label:'Applies to',render:r=>({staff:'Staff',student:'Student',both:'Both'}[r.category_type]||r.category_type)},
          {key:'visible_roles',label:'Visible to',render:roleNames},
        ]} />}
      {tab === 'sop' && <SopTemplatesTab campuses={campuses} />}
      {tab === 'vendors' && <ResourceCrud apiBase="/master/vendors" itemLabel="Vendor"
        fields={[{key:'name',label:'Vendor Name',required:true,placeholder:'e.g. ABC Technologies'},{key:'service_categories',label:'Service Categories',placeholder:'e.g. Laptop Repairs, Printers'},{key:'contact_person',label:'Contact Person'},{key:'phone',label:'Contact Number'},{key:'email',label:'Email',type:'email'},{key:'sla_days',label:'SLA (Working Days)',type:'number',placeholder:'5'}]}
        cols={[{key:'name',label:'Vendor'},{key:'service_categories',label:'Categories'},{key:'contact_person',label:'Contact'},{key:'phone',label:'Phone'},{key:'sla_days',label:'SLA (days)'}]} />}
      {tab === 'itresources'&& <ITResourcesTab campuses={campuses.filter(c => c.active)} isAdmin={isAdmin} />}
      {tab === 'accessories'&& <AccessoriesTab />}
      {tab === 'meetingtypes' && <MeetingTypesTab isAdmin={isAdmin} />}
      {tab === 'welcomeart'   && <WelcomeImagesTab isAdmin={isAdmin} />}
      {tab === 'gwous'        && <GoogleOuTab isAdmin={isAdmin} />}
      {tab === 'gwgroups'     && <GoogleGroupsTab isAdmin={isAdmin} />}
      {tab === 'holidays'   && <HolidaysTab campuses={campuses.filter(c => c.active)} isAdmin={isAdmin} />}
    </div>
  );
}
